import { withFormik } from "formik";
import * as yup from 'yup';
import SparkLocationsForm, { AutofillFieldState, isSandboxConfiguring, loadPOSConfigForLocationId, LocationSubStep, ManageSparkLocationsFormData, ManageSparkLocationsFormProps, maxLocationNameLength } from "./SparkLocationsForm";
export { isLocationIdSentinel, isSandboxConfiguring } from "./SparkLocationsForm";
import { createEmptyApplication, findApplicationWithFeatures, getFeatureFlags, getPOSConfig, toggleAllowsTemplating, updateApplication, updateApplicationFeatureFlagSettings, updatePOSConfig } from "../../../../../shared/api/controllers/applications";
import { addAppliedApplicationTemplate, getAppliedApplicationTemplatesForApp, updateAppliedApplicationTemplate } from "../../../../../shared/api/controllers/appliedApplicationTemplates";
import UpdateAppliedApplicationTemplateFormRequest from "../../../../../shared/models/templating/api/UpdateAppliedApplicationTemplateFormRequest";
import { createTemplateConfiguration, getAvailableTemplateConfigurations, getOrganizationTemplateConfigurations, updateTemplateConfiguration } from "../../../../../shared/api/controllers/templateConfigurations";
import TemplateConfigurationModel from "../../../../../shared/models/templating/api/TemplateConfigurationModel";
import TemplateFormSectionModel from "../../../../../shared/models/templating/api/TemplateFormSectionModel";
import { v4 as uuidv4 } from 'uuid';
import { getAIAnsweringModuleId, getFoodOrderingModuleId, getHealthcareSchedulingModuleId } from "../../../../../shared/constants/ModuleConsts";

import TemplateFormFieldValueModel from "../../../../../shared/models/templating/api/TemplateFormFieldValueModel";
import { AIAnsweringFeatureFlagId, DisableLegacyCheckoutFlowFeatureFlagId, FoodOrderingFeatureFlagId, GenAIFoodOrdering, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from "../../../../../shared/constants/featureFlagIds";
import UpdateApplicationRequest from "../../../../../shared/models/applications/api/UpdateApplicationRequest";
import { aiAnsweringCommonAppName, aiAnsweringCustomModuleName, aiDentalSchedulingCommonAppName, aiDentalSchedulingCustomModuleName, aiOrderingCommonAppName, aiOrderingCustomModuleName } from "../../../../../shared/constants/sparkConstants/moduleConsts";
import { getDefaultAssistantConfig, FormFieldConfiguration } from "../../../../../shared/constants/sparkConstants/defaultSparkAssistantConfigurations";
import { autoGenerateFormFieldValues, FormFillField } from "../../../../../shared/api/controllers/formFill";
import { FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE, FORM_FILL_FIELD_LABELS, FORM_FILL_FIELD_DESCRIPTIONS, DAY_KEYS, dayHoursDescription, TIME_ZONE_DESCRIPTIONS, SH_LOCATION_NAME_PROMPT, LOC_LOCATION_NAME_PROMPT, SH_CLIENT_NAME_PROMPT, LOC_CLIENT_NAME_PROMPT } from "../../../../../shared/constants/sparkConstants/formFillFieldConfig";
import { FoodOrderingGenAIModelSettingId, FoodOrderingGenAIPromptOverrideSettingId } from "../../../../../shared/constants/featureFlagSettingIds";
import { initiateMenuUpdate, syncMenus } from "../../../../../shared/api/controllers/voicifyMenuItems";
import UpdateMenuRequest from "../../../../../shared/models/nlp/menu/UpdateMenuRequest";
import { SyncMenusRequest } from "../../../../../shared/models/nlp/menu/SyncMenusRequest";
import { POSNames } from "../../../../../shared/models/nlp/PosNames";
import { getApplicationEnvironments } from "../../../../../shared/api/controllers/applicationEnvironment";
import { updateEnvironmentSettings } from "../../../../../shared/api/controllers/environments";
import TemplateFormFieldModel from "../../../../../shared/models/templating/api/TemplateFormFieldModel";
import AppliedApplicationTemplateModel from "../../../../../shared/models/templating/api/AppliedApplicationTemplateModel";
import { dedupeFieldValuesForField, fieldsMatch, findFieldValueForField, getFieldIdentity } from "../../../../../shared/utils/templateFieldMatching";
import { evaluateConditionalDefault } from "../../../../../shared/utils/conditionalDefaultUtils";
import { isUnknownValue } from "../../dashboard/autoGenerateFormField/autoGenerateResultUtils";
import { provisionLocationPhone } from "../../../services/locationPhoneProvisioning";
import { getLocationAutofillVariable, isStoreAddressField, needsOrderingCloneAddressGeneration } from "./locationAddressUtils";

export { dedupeFieldValuesForField, fieldsMatch, findFieldValueForField, getFieldIdentity };

/**
 * Associated variables that are allowed to be autogenerated when a location is being cloned.
 * All other fields will have their values copied from the selected source location.
 */
export const CLONE_AUTOGENERATE_ALLOWLIST = new Set<string>([
    '{Address}',                           // Store Address (AI Ordering)
    '{ClientName}',                        // Office Name
    '{TransferTarget}',                    // Transfer Target
    '{EmergencyTransferTarget}',           // Emergency Transfer Target
    '{OfficeGoogleMapsLink}',              // Google Maps Link
    '{MONDAY}',                            // Monday Hours
    '{TUESDAY}',                           // Tuesday Hours
    '{WEDNESDAY}',                         // Wednesday Hours
    '{THURSDAY}',                          // Thursday Hours
    '{FRIDAY}',                            // Friday Hours
    '{SATURDAY}',                          // Saturday Hours
    '{SUNDAY}',                            // Sunday Hours
    '{TIME_ZONE}',                         // Time Zone
    '{Webhook-Time-Zone}',                 // Webhook Time Zone
    '{OfficeEmail}',                       // Office Email
    '{LocationName}',                      // Restaurant Location Name
    '{Webhook-SearchAddress-Latitude}',    // Location Latitude
    '{Webhook-SearchAddress-Longitude}',   // Location Longitude
    '{OfficeWebsite}',                     // Office Website
    '{AboutThePracticePrompt}',            // About the Practice
    '{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}', // Location Non-Ordering Info
]);

/** Ordering fields that must never inherit values from a clone source. */
export const ORDERING_CLONE_ALWAYS_AUTOGENERATE_ALLOWLIST = new Set<string>([
    '{Address}',
]);

/**
 * Field titles that are allowed to be autogenerated when a location is being cloned.
 * Serves as a fallback matching system when associatedVariable is empty.
 */
export const CLONE_AUTOGENERATE_TITLES_ALLOWLIST = new Set<string>([
    'Store Address',
    'Address',
    'Office Name',
    'Transfer Target',
    'Emergency Transfer Target',
    'Google Maps Link',
    'Office Google Maps Link',
    'Monday Hours',
    'Tuesday Hours',
    'Wednesday Hours',
    'Thursday Hours',
    'Friday Hours',
    'Saturday Hours',
    'Sunday Hours',
    'Time Zone',
    'Office Email',
    'Restaurant Location Name',
    'Location Latitude',
    'Location Longitude',
    'Office Phone Number',
    'Emergency Phone Number',
    'Office Website',
    'About the Practice',
    'Location Non-Ordering Info',
]);

/**
 * Associated variables that should use their default/existing configurations.
 * They should be neither cloned nor autogenerated.
 */
export const CLONE_USE_DEFAULT_ALLOWLIST = new Set<string>([
    '{{session.returntostorenumber}}',     // Transfer Target Phone Number (AI Ordering)
    '{Webhook-Application-Id}',            // Application ID
    '{Webhook-Application-Secret}',        // Application Secret
    '{Webhook-Restaurant-Id}',             // Store ID / POS Location ID
    '{defaultOfficeId}',                   // Default Office ID (Dental)
    '{Webhook-Restaurant-PhoneNumber}',    // Restaurant Phone Number (AI Ordering)
    '{Webhook-Restaurant-Name}',           // Restaurant Name (AI Ordering)
    '{{OfficeAppointmentToEmail}}',        // To Email Addresses (Dental Scheduling)
]);

/**
 * Field titles that should use their default/existing configurations.
 */
export const CLONE_USE_DEFAULT_TITLES_ALLOWLIST = new Set<string>([
    'Transfer Target Phone Number',
    'Application ID',
    'Application Secret',
    'Location Id',
    'Location ID',
    'Store ID',
    'Default Office ID',
    'Restaurant Phone Number',
    'Restaurant Name',
    'To Email Addresses',
]);

export function getAppliedTemplateConfigurationId(appliedTemplate: AppliedApplicationTemplateModel | any): string | undefined {
    return appliedTemplate?.templateForm?.templateConfigurationId
        || appliedTemplate?.templateForm?.templateConfiguration?.id;
}

export function isOrgOwnedAppliedTemplate(appliedTemplate: AppliedApplicationTemplateModel | any, organizationId: string): boolean {
    return appliedTemplate?.templateForm?.templateConfiguration?.organizationId === organizationId;
}

export function getManagedLocationAppliedTemplate(appliedTemplates: AppliedApplicationTemplateModel[] = [], organizationId: string, preferredTemplateConfigurationId?: string): AppliedApplicationTemplateModel | undefined {
    return appliedTemplates.find(appliedTemplate =>
        preferredTemplateConfigurationId
        && getAppliedTemplateConfigurationId(appliedTemplate) === preferredTemplateConfigurationId
        && isOrgOwnedAppliedTemplate(appliedTemplate, organizationId))
        || appliedTemplates.find(appliedTemplate => isOrgOwnedAppliedTemplate(appliedTemplate, organizationId));
}

export function hasDirectCoreAppliedTemplate(appliedTemplates: AppliedApplicationTemplateModel[] = [], coreModuleIds: string[] = []): boolean {
    return appliedTemplates.some(appliedTemplate => {
        const templateConfigurationId = getAppliedTemplateConfigurationId(appliedTemplate);
        return !!templateConfigurationId && coreModuleIds.some(coreModuleId => coreModuleId === templateConfigurationId);
    });
}

/**
 * Resolves the value to persist for a single template form field when updating an existing
 * location app's applied template.
 *
 * Priority:
 *  1. Deselected field  → reset to schema default (field moved back to shared config)
 *  2. Selected office/store ID field with a Configuration-tab value → use that value
 *  3. Field whose associatedVariable was produced by the AI autofill run → use AI-generated value
 *  4. Selected field with an existing saved value → preserve the existing value
 *  5. Selected field with no existing saved value (newly added) → seed from defaultConfig
 *  6. Non-selected field with an existing saved value → preserve the existing value
 *  7. Fallback → schema default value
 *
 * @param autofillVariables - Set of associatedVariable strings that the AI actually generated
 *   values for in this run. Only fields whose variable is in this set are overwritten; all
 *   other fields follow the normal preserve-existing-value logic.
 */
export function resolveLocationFieldValue(params: {
    formField: TemplateFormFieldModel;
    isFieldSelected: boolean;
    isFieldDeselected: boolean;
    existingValue: string | undefined | null;
    defaultConfig: Record<string, any>;
    autofillVariables?: Set<string>;
    configuredLocationId?: string;
    /**
     * The value that was saved in the shared config applied template BEFORE the sentinel
     * string was stamped over it during the current update.  Supplied only on the update
     * path when the field is newly selected as location-specific; allows the location app
     * to be seeded with the real shared-config value instead of a static default.
     */
    sharedConfigValue?: string | undefined;
}): string | undefined {
    const { formField, isFieldSelected, isFieldDeselected, existingValue, defaultConfig, autofillVariables, configuredLocationId, sharedConfigValue } = params;

    if (isFieldDeselected) {
        return formField.defaultValue;
    }

    const isConfiguredLocationIdField = formField.associatedVariable === '{defaultOfficeId}'
        || formField.associatedVariable === '{Webhook-Restaurant-Id}'
        || formField.title === 'Default Office ID'
        || formField.title === 'Location ID'
        || formField.title === 'Admin - Location ID';
    if (isFieldSelected && isConfiguredLocationIdField && configuredLocationId !== undefined) {
        return configuredLocationId;
    }

    // If this field's variable was actually produced by the AI, overwrite the existing value.
    // This is intentionally checked before the selected-field branch so it applies regardless
    // of whether the field is location-specific or shared.
    const wasAutofilled = !!(formField.associatedVariable && autofillVariables?.has(formField.associatedVariable));
    if (wasAutofilled) {
        const aiValue = defaultConfig[formField.associatedVariable];
        if (aiValue !== undefined && aiValue !== null && aiValue !== '') {
            return typeof aiValue === 'string' ? aiValue : String(aiValue);
        }
    }

    if (isFieldSelected) {
        // Guard against a stored sentinel being treated as a real value.  This can occur when a
        // field has been toggled location-specific → shared → location-specific across multiple
        // saves; the location app retains the sentinel string from the earlier cycle.
        const existingIsSentinel = existingValue === formField.associatedVariable;
        if (existingValue !== undefined && existingValue !== null && !existingIsSentinel) {
            return existingValue;
        }
        // For a field that is newly location-specific (no meaningful existing value on the
        // location app), carry over the value that was saved in the shared config before it was
        // overwritten with a sentinel.  Only use it when it is itself not a sentinel.
        const sharedConfigIsSentinel = sharedConfigValue === formField.associatedVariable;
        if (sharedConfigValue !== undefined && sharedConfigValue !== null && sharedConfigValue !== '' && !sharedConfigIsSentinel) {
            return sharedConfigValue;
        }
        // Newly added field — seed with default config value
        const configKey =
            formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                ? formField.associatedVariable
                : formField.title;
        const configValue = defaultConfig[configKey];
        if (configValue !== undefined && configValue !== null && configValue !== '') {
            return typeof configValue === 'string' ? configValue : String(configValue);
        }
        return formField.defaultValue;
    }

    // Field is not location-specific — preserve existing saved value if present
    if (existingValue !== undefined && existingValue !== null) {
        return existingValue;
    }

    return formField.defaultValue;
}

/**
 * Merges an outbound feature flag into an existing feature flags array without
 * replacing any other flags.  Any prior outbound entry is removed first so the
 * result always contains exactly one entry for that flag.
 *
 * Used in `updateExistingLocationApp` to avoid a full-replace PUT from silently
 * dropping flags that this form does not manage.
 */
export function mergeOutboundFeatureFlag(
    existingFlags: { id: string; name?: string; featureFlagSettings?: any[] }[],
    outboundFlagId: string,
    outboundFlag: { id?: string; name?: string; featureFlagSettings?: any[] }
): { id: string; name?: string; featureFlagSettings?: any[] }[] {
    return [
        ...existingFlags.filter(f => f.id !== outboundFlagId),
        {
            id: outboundFlagId,
            name: outboundFlag.name,
            featureFlagSettings: outboundFlag.featureFlagSettings
        }
    ];
}

// ============================================================================
// Module-level pure helpers — extracted from handleSubmit so they can be
// imported and unit-tested directly.  The withFormik wrapper calls these
// exactly as before; the only change is that they are no longer closures.
// ============================================================================

export function getCommonAppNameForAssistantType(assistantType: string): string {
    switch (assistantType) {
        case 'ai-ordering': return aiOrderingCommonAppName;
        case 'dental-scheduling': return aiDentalSchedulingCommonAppName;
        default: return aiAnsweringCommonAppName;
    }
}

export function getSelectedModuleId(assistantType: string): string {
    switch (assistantType) {
        case 'ai-answering': return getAIAnsweringModuleId();
        case 'ai-ordering': return getFoodOrderingModuleId();
        case 'dental-scheduling': return getHealthcareSchedulingModuleId();
        default: return getAIAnsweringModuleId();
    }
}

export function getFeatureFlagForAssistantType(assistantType: string): string {
    switch (assistantType) {
        case 'ai-answering': return AIAnsweringFeatureFlagId;
        case 'ai-ordering': return FoodOrderingFeatureFlagId;
        case 'dental-scheduling': return HealthcareSchedulingFeatureFlagId;
        default: return AIAnsweringFeatureFlagId;
    }
}

export function getCustomModuleName(assistantType: string): string {
    switch (assistantType) {
        case 'ai-answering': return aiAnsweringCustomModuleName;
        case 'ai-ordering': return aiOrderingCustomModuleName;
        case 'dental-scheduling': return aiDentalSchedulingCustomModuleName;
        default: return aiAnsweringCustomModuleName;
    }
}

export function buildGenAISettings(flag: { featureFlagSettings?: any[];[key: string]: any }): any[] {
    return (flag.featureFlagSettings ?? []).map((setting: any) => {
        if (setting.id === FoodOrderingGenAIModelSettingId) {
            return { ...setting, value: 'gemini-3.5-flash' };
        }
        if (setting.id === FoodOrderingGenAIPromptOverrideSettingId) {
            return { ...setting, value: '{{constant.new_ordering_prompt}}' };
        }
        if (setting.name === 'Generative Assistant Ordering') {
            return { ...setting, value: 'true' };
        }
        return setting;
    });
}

export function buildManageMenuFlagsToSet(existingFlags: any[] = []): any[] {
    const existingFoodOrderingFlag = existingFlags.find((f: any) => f.id === FoodOrderingFeatureFlagId);
    const existingGenAIFoodOrderingFlag = existingFlags.find((f: any) => f.id === GenAIFoodOrdering);
    return [
        ...existingFlags.filter((f: any) => f.id !== FoodOrderingFeatureFlagId && f.id !== GenAIFoodOrdering),
        existingFoodOrderingFlag ?? { id: FoodOrderingFeatureFlagId },
        existingGenAIFoodOrderingFlag ?? { id: GenAIFoodOrdering },
    ];
}

export function hasExistingPOSConfig(result: { resultType?: string; data?: { data?: string } }): boolean {
    return result.resultType === 'Ok' && !!result.data?.data?.trim();
}

export function canCreatePOSConfig(result: { resultType?: string; data?: { data?: string } }): boolean {
    return (result.resultType === 'Ok' && !result.data?.data?.trim())
        || result.resultType === 'Invalid'
        || result.resultType === 'NotFound';
}

/**
 * Builds the POS configuration for a newly-created location app.
 * When cloning, all source settings are preserved except location-specific
 * identifiers and application credentials, which must always belong to the
 * new location app.
 */
export function buildNewLocationPOSConfig(
    applicationId: string,
    applicationSecret: string,
    sourceConfig?: string,
    storeId?: string
): string {
    let config: Record<string, any> = {
        'voicify_x_api-key': ''
    };

    if (sourceConfig?.trim()) {
        const parsedSourceConfig = JSON.parse(sourceConfig);
        if (!parsedSourceConfig || typeof parsedSourceConfig !== 'object' || Array.isArray(parsedSourceConfig)) {
            throw new Error('The source POS configuration is invalid.');
        }
        config = { ...parsedSourceConfig };

        const hasVoicifyApiKey = Object.prototype.hasOwnProperty.call(config, 'voicify_x_api-key')
            || Object.prototype.hasOwnProperty.call(config, 'voicify_x_api_key');
        if (!hasVoicifyApiKey) {
            config['voicify_x_api-key'] = '';
        }

        // POS integrations use either key for the location identifier. Only
        // replace keys present in the source so the cloned config keeps the
        // schema expected by that integration.
        if (storeId !== undefined) {
            if (Object.prototype.hasOwnProperty.call(config, 'storeId')) {
                config.storeId = storeId;
            }
            if (Object.prototype.hasOwnProperty.call(config, 'locationId')) {
                config.locationId = storeId;
            }
        }
    }

    return JSON.stringify({
        ...config,
        applicationId,
        applicationSecret
    }, null, 4);
}

const maxShortDescriptionLength = 160;

/** Short descriptions are capped at 160 characters by the API, so long location names are truncated here. */
export function toShortDescription(name: string): string {
    return name.length > maxShortDescriptionLength ? name.substring(0, maxShortDescriptionLength) : name;
}

/**
 * Pure decision function for the Manage Menu app deduplication logic on the update path.
 * Determines whether to use the linked ID on the location app, fall back to a name match,
 * or create a brand-new Manage Menu app — without performing any API calls.
 */
export function resolveManageMenuAppDecision(
    existingApp: { manageMenuApplicationId?: string; name: string },
    manageMenuApps: { id: string; name: string }[]
): { decision: 'use-linked-id' | 'use-name-match' | 'create-new'; matchedApp?: { id: string; name: string } } {
    const linkedId = existingApp.manageMenuApplicationId;
    if (linkedId) {
        const matchedApp = manageMenuApps.find((app) => app.id === linkedId);
        return { decision: 'use-linked-id', matchedApp };
    }
    const manageMenuAppName = `${existingApp.name} - Manage Menu`;
    const nameMatchedApp = manageMenuApps.find((app) => app.name === manageMenuAppName);
    if (nameMatchedApp) {
        return { decision: 'use-name-match', matchedApp: nameMatchedApp };
    }
    return { decision: 'create-new' };
}

const ManageSparkLocationsForm = withFormik<ManageSparkLocationsFormProps, ManageSparkLocationsFormData>({
    mapPropsToValues: props => (
        {
            commonAppName: aiAnsweringCommonAppName,
            locationNames: [""],
            selectedFormFields: props.locationAppFormFields ? props.locationAppFormFields : [],
            deselectedFormFields: [],
            assistantType: "",
            assistantSetupType: "",
            pmsName: "",
            posNameOrdering: "",
            storeIds: {},
            websiteUrl: '',
            autofillPrompts: {},
            locationWebsiteUrls: {},
            // New: per-location autogenerate flags.
            // Initial setup (no commonApp yet) → all checked by default (index 0 pre-seeded).
            // Subsequent visits (commonApp exists) → all unchecked so users must opt in per location.
            locationAutogenerateEnabled: !props.commonApp ? { 0: true } : {},
            // Derived flag kept for backward-compat with validation and submit logic.
            autogenerateEnabled: !props.commonApp,
            locationCloneEnabled: {},
            locationCloneSourceAppIds: {},
            locationCreatePhoneNumber: {},
            skipPOSConfigLocationIndexes: [],
        }
    ),
    validationSchema: (props: ManageSparkLocationsFormProps) => {
        const isNewOrg = !props.commonApp;
        return yup.object().shape(
            {
                locationNames: yup
                    .array()
                    .of(yup.string().nullable().optional())
                    .test(
                        'at-least-one-location',
                        'At least one location is required',
                        (val) => Array.isArray(val) && val.some(name => name && name.trim().length > 0)
                    )
                    .test(
                        'location-name-length',
                        `Location names must be ${maxLocationNameLength} characters or fewer`,
                        (val) => !Array.isArray(val) || val.every(name => !name || name.length <= maxLocationNameLength)
                    ),
                assistantType: yup.string().required('Assistant type is required'),
                assistantSetupType: isNewOrg
                    ? yup.string().required('Assistant setup type is required')
                    : yup.string().notRequired(),
                pmsName: yup.string().when('assistantType', {
                    is: (assistantType: string) => assistantType === 'dental-scheduling' && isNewOrg,
                    then: (schema) => schema.required('PMS Name is required for AI Dental Scheduling'),
                    otherwise: (schema) => schema.notRequired()
                }),
                posNameOrdering: yup.string().when('assistantType', {
                    is: (assistantType: string) => assistantType === 'ai-ordering' && isNewOrg,
                    then: (schema) => schema.required('POS Name is required for AI Ordering'),
                    otherwise: (schema) => schema.notRequired()
                }),
                storeIds: yup.mixed().notRequired(),
                locationAutogenerateEnabled: yup.mixed().notRequired(),
                locationCloneEnabled: yup.mixed().notRequired(),
                locationCloneSourceAppIds: yup.mixed().test(
                    'required-clone-source-apps',
                    'Please select a location to clone from',
                    function (val: Record<number, string> | undefined) {
                        const parent = this.parent as any;
                        const locationNames: string[] = parent.locationNames ?? [];
                        const rawFlags: Record<string, boolean> = parent.locationCloneEnabled ?? {};
                        const sourceAppIds = (val ?? {}) as Record<string, string>;

                        for (let i = 0; i < locationNames.length; i++) {
                            const name = locationNames[i];
                            if (!name || !name.trim()) continue;
                            const isEnabled = rawFlags[i] === true || rawFlags[String(i)] === true;
                            if (!isEnabled) continue;

                            const appId = (sourceAppIds[i] ?? sourceAppIds[String(i)] ?? '').trim();
                            if (!appId) {
                                return this.createError({ message: 'Please select a location to clone from' });
                            }
                        }
                        return true;
                    }
                ),
                websiteUrl: yup.string().test(
                    'website-url-when-autogenerate',
                    'Organization Website URL is required when Autogenerate content is enabled',
                    function (val) {
                        const parent = this.parent as any;
                        const assistantType: string = parent.assistantType ?? '';
                        if (assistantType !== 'ai-ordering' && assistantType !== 'dental-scheduling') return true;

                        const rawFlags: Record<string, boolean> = parent.locationAutogenerateEnabled ?? {};
                        const anyEnabled = Object.values(rawFlags).some(Boolean);
                        if (!anyEnabled) return true;

                        // URL is required when any location has autogenerate enabled
                        if (!val || !val.trim()) {
                            return this.createError({ message: 'Organization Website URL is required when Autogenerate content is enabled' });
                        }
                        if (!/^https?:\/\/.+\..+/.test(val.trim())) {
                            return this.createError({ message: 'Please enter a valid website URL (e.g. https://yourbusiness.com)' });
                        }
                        return true;
                    }
                ),
                locationWebsiteUrls: yup.mixed().test(
                    'required-and-valid-location-urls',
                    'Location website URL is required for locations with Autogenerate enabled',
                    function (val: Record<number, string> | undefined) {
                        const parent = this.parent as any;
                        const assistantType: string = parent.assistantType ?? '';
                        if (assistantType !== 'ai-ordering' && assistantType !== 'dental-scheduling') return true;

                        const locationNames: string[] = parent.locationNames ?? [];
                        // locationAutogenerateEnabled keys may be strings when coming from Formik — normalise to numbers
                        const rawFlags: Record<string, boolean> = parent.locationAutogenerateEnabled ?? {};
                        const locationUrls = (val ?? {}) as Record<string, string>;

                        for (let i = 0; i < locationNames.length; i++) {
                            const name = locationNames[i];
                            if (!name || !name.trim()) continue;
                            // Check both numeric and string key forms
                            const isEnabled = rawFlags[i] === true || rawFlags[String(i)] === true;
                            const rawCloneFlags: Record<string, boolean> = parent.locationCloneEnabled ?? {};
                            const isOrderingClone = assistantType === 'ai-ordering'
                                && (rawCloneFlags[i] === true || rawCloneFlags[String(i)] === true);
                            if (!isEnabled && !isOrderingClone) continue;

                            const url = (locationUrls[i] ?? locationUrls[String(i)] ?? '').trim();
                            if (!url) {
                                if (needsOrderingCloneAddressGeneration(assistantType, isOrderingClone)) {
                                    return this.createError({ message: `Location website URL is required to generate the address for "${name}"` });
                                }
                                continue;
                            }
                            if (!/^https?:\/\/.+\..+/.test(url)) {
                                return this.createError({ message: `Please enter a valid website URL for "${name}" (e.g. https://yourbusiness.com)` });
                            }
                        }
                        return true;
                    }
                )
            });
    },
    handleSubmit: async (values, { props, setStatus }) => {

        // Track what actually happened for the success message
        const commonAppWasCreated = !props.commonApp;
        const createdNames: string[] = [];
        const updatedNames: string[] = [];
        const posConfigErrors: string[] = [];
        const phoneProvisioningErrors: string[] = [];
        const provisionedPhoneNumbers: string[] = [];
        // Fields moved back to shared configuration in this submit (update path only)
        const newlySharedFields = props.commonApp ? (values.deselectedFormFields ?? []) : [];
        // Accumulate autofill fields that were skipped due to API errors across all runs
        const allAutofillErrors: Array<{ fieldLabel: string; error: string; appName?: string; retryAttempted?: boolean }> = [];
        // Accumulate autofill fields that were successfully generated across all runs
        const allAutofillSuccesses: Array<{ fieldLabel: string; generatedValue: string; appName?: string }> = [];
        // Variables already fetched by the shared-config autofill call; location calls skip these
        // to avoid making duplicate API calls for the same field.
        const sharedAutofillVariables = new Set<string>();
        // Field values from the shared config applied template captured BEFORE sentinel strings
        // are written into it during updateCommonAppAndAppliedAppTemplate.  When a field is newly
        // checked as location-specific on an update, the location app may have no prior value for
        // it; these pre-stamp values allow us to seed the location app with the real value the
        // user had in the shared config rather than falling back to a static default.
        let sharedConfigPreStampValues: TemplateFormFieldValueModel[] = [];

        /**
         * Calls the AI formfill endpoint for each eligible field in the allowlist for the
         * current assistant type, merging results into a copy of baseConfig.
         * Non-fatal: any field that errors is recorded in skippedWithErrors and skipped.
         * Returns {} config if websiteUrl is empty or assistantType has no allowlist.
         */
        const resolveAiAutofillConfig = async (
            baseConfig: Record<string, string>,
            sections: TemplateFormSectionModel[],
            locationName: string,
            websiteUrl: string,
            skipVariables: Set<string> = new Set()
        ): Promise<{
            config: Record<string, string>;
            succeededFields: Array<{ fieldLabel: string; generatedValue: string; appName?: string }>;
            skippedWithErrors: Array<{ fieldLabel: string; error: string; appName?: string }>;
            succeededVariables: Set<string>;
            /** Fields that got an Ok response but with value === 'Unknown' — eligible for retry. */
            unknownFields: Array<{ variable: string; fieldLabel: string }>;
        }> => {
            const config: Record<string, string> = { ...baseConfig };
            const succeededFields: Array<{ fieldLabel: string; generatedValue: string; appName?: string }> = [];
            const skippedWithErrors: Array<{ fieldLabel: string; error: string; appName?: string }> = [];
            const succeededVariables = new Set<string>();
            const unknownFields: Array<{ variable: string; fieldLabel: string }> = [];

            if (!websiteUrl || !values.assistantType) {
                return { config, succeededFields, skippedWithErrors, succeededVariables, unknownFields };
            }

            const allowlist = FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE[values.assistantType];
            if (!allowlist) {
                return { config, succeededFields, skippedWithErrors, succeededVariables, unknownFields };
            }

            const orgId = props.orgContainer.state.currentOrganization.id;

            // Build list of fields to call — only ones present in sections and having a description
            const allSectionFields = sections.flatMap(s => s.templateFormFields ?? []);

            // Determine the set of variables to actually call the API for.
            // by only firing one API call and copying the result to the sibling afterwards.
            type EligibleEntry = { variable: string; fieldLabel: string; prompt: string };
            const eligible: EligibleEntry[] = [];
            let webhookTimezoneDeferred: string | null = null; // variable name to copy timezone into

            for (const variable of allowlist) {
                // Skip if user opted this field out via per-field toggles
                if (values.autofillPrompts !== undefined && !(variable in values.autofillPrompts)) {
                    continue;
                }
                // Skip variables already fetched by a prior call (e.g. the shared-config call)
                if (skipVariables.has(variable)) {
                    continue;
                }
                // Check whether this variable corresponds to a real field in the sections.
                // This applies to all variables including day-of-week, time zone, and DISABLED_DAYS —
                // if the field is not present in the sections passed to this call (e.g. because it was
                // filtered out as a location-specific field for the shared-config call), skip it so it
                // is only generated in the correct per-location call.
                const sectionField = allSectionFields.find(f => f.associatedVariable === variable);
                if (!sectionField) {
                    continue;
                }

                // {TIME_ZONE} and {Webhook-Time-Zone} share an identical prompt and value.
                // Only fire one API call; copy the result to the sibling after all calls settle.
                const isTimezoneVariable = variable === '{TIME_ZONE}' || variable === '{Webhook-Time-Zone}';
                if (isTimezoneVariable) {
                    if (eligible.some(e => e.variable === '{TIME_ZONE}' || e.variable === '{Webhook-Time-Zone}')) {
                        // The sibling was already queued — defer this one for result-copying
                        webhookTimezoneDeferred = variable;
                        continue;
                    }
                }

                // Build the prompt description
                let description: string | undefined;
                if (variable === '{LocationName}' || variable === '{ClientName}') {
                    const isSharedConfig = !locationName || locationName === aiOrderingCommonAppName || locationName === aiDentalSchedulingCommonAppName || locationName === aiAnsweringCommonAppName;
                    if (isSharedConfig) {
                        description = variable === '{LocationName}' ? SH_LOCATION_NAME_PROMPT : SH_CLIENT_NAME_PROMPT;
                    } else {
                        description = variable === '{LocationName}' ? LOC_LOCATION_NAME_PROMPT : LOC_CLIENT_NAME_PROMPT;
                    }
                } else if (values.autofillPrompts?.[variable]) {
                    description = values.autofillPrompts[variable];
                } else {
                    const dayKey = DAY_KEYS.find(d => variable === `{${d}}`);
                    if (dayKey) {
                        description = dayHoursDescription(dayKey);
                    } else if (isTimezoneVariable) {
                        description = TIME_ZONE_DESCRIPTIONS[values.assistantType];
                    } else {
                        description = FORM_FILL_FIELD_DESCRIPTIONS[variable];
                    }
                }

                if (!description) continue;

                eligible.push({
                    variable,
                    fieldLabel: FORM_FILL_FIELD_LABELS[variable] ?? variable,
                    prompt: description.replace(/\{websiteUrl\}/g, websiteUrl).replace(/<URL>/g, websiteUrl),
                });
            }

            // Fire all eligible API calls in parallel
            const results = await Promise.allSettled(
                eligible.map(({ variable, prompt }) =>
                    autoGenerateFormFieldValues(orgId, { websiteUrl, prompt, associatedVariable: variable })
                )
            );

            // Merge results into config / succeeded / skipped arrays
            let timezoneResult: { value?: string; error?: string } | null = null;
            for (let i = 0; i < eligible.length; i++) {
                const { variable, fieldLabel } = eligible[i];
                const settled = results[i];
                const isTimezoneVariable = variable === '{TIME_ZONE}' || variable === '{Webhook-Time-Zone}';

                if (settled.status === 'fulfilled') {
                    const result = settled.value;
                    if (result.resultType === 'Ok' && result.data?.value && !isUnknownValue(result.data.value)) {
                        const val = result.data.value;

                        config[variable] = val;
                        succeededFields.push({ fieldLabel, generatedValue: val, appName: locationName || undefined });
                        succeededVariables.add(variable);
                        if (isTimezoneVariable) timezoneResult = { value: val };
                    } else {
                        const errMsg = 'Could not retrieve a value from the website.';
                        skippedWithErrors.push({ fieldLabel, error: errMsg, appName: locationName || undefined });
                        if (isTimezoneVariable) timezoneResult = { error: errMsg };
                        // Track genuine "no data found" responses separately from hard API errors so
                        // the retry wrapper can attempt a fallback URL for these fields only.
                        if (result.resultType === 'Ok' && result.data?.value && isUnknownValue(result.data.value)) {
                            unknownFields.push({ variable, fieldLabel });
                        }
                    }
                } else {
                    const errMsg = settled.reason?.toString() ?? 'Unknown error';
                    skippedWithErrors.push({ fieldLabel, error: errMsg, appName: locationName || undefined });
                    if (isTimezoneVariable) timezoneResult = { error: errMsg };
                    // Hard API errors are NOT added to unknownFields — a network failure on retry
                    // would likely fail again for the same reason.
                }
            }

            // Copy timezone result to the deferred sibling variable ({TIME_ZONE} ↔ {Webhook-Time-Zone})
            if (webhookTimezoneDeferred && timezoneResult) {
                const siblingLabel = FORM_FILL_FIELD_LABELS[webhookTimezoneDeferred] ?? webhookTimezoneDeferred;
                if (timezoneResult.value) {
                    config[webhookTimezoneDeferred] = timezoneResult.value;
                    succeededFields.push({ fieldLabel: siblingLabel, generatedValue: timezoneResult.value, appName: locationName || undefined });
                    succeededVariables.add(webhookTimezoneDeferred);
                } else {
                    skippedWithErrors.push({ fieldLabel: siblingLabel, error: timezoneResult.error ?? 'Could not retrieve a value from the website.', appName: locationName || undefined });
                    // The sibling inherits the same Unknown classification as the primary timezone call.
                    if (timezoneResult.error === 'Could not retrieve a value from the website.') {
                        unknownFields.push({ variable: webhookTimezoneDeferred, fieldLabel: siblingLabel });
                    }
                }
            }

            return { config, succeededFields, skippedWithErrors, succeededVariables, unknownFields };
        };

        /**
         * Wraps resolveAiAutofillConfig with a single fallback-URL retry pass.
         *
         * Only fires on initial configuration (create path).  For each field that returned
         * Ok + "Unknown" on the primary pass, a second call is made using the fallback URL.
         * Fields that succeed on retry are merged into the result as normal successes.
         * Fields that fail both passes land in skippedWithErrors with retryAttempted: true,
         * which causes the results modal to show "Both URLs tried" next to "Unknown".
         *
         * Hard API errors (non-Ok, network failures) are NOT retried — they are passed through
         * from the primary result unchanged.
         *
         * If primaryUrl === fallbackUrl (normalised), or fallbackUrl is empty, the function
         * behaves identically to a plain resolveAiAutofillConfig call.
         */
        const resolveAiAutofillConfigWithRetry = async (
            baseConfig: Record<string, string>,
            sections: TemplateFormSectionModel[],
            locationName: string,
            primaryUrl: string,
            fallbackUrl: string,
            skipVariables: Set<string> = new Set(),
            retryNoteDirection: 'toLocationSpecific' | 'toSharedConfig'
        ) => {
            // Primary pass
            const firstPass = await resolveAiAutofillConfig(
                baseConfig, sections, locationName, primaryUrl, skipVariables
            );

            // Determine whether a retry is worth attempting
            const normalizedPrimary = primaryUrl.trim().toLowerCase();
            const normalizedFallback = fallbackUrl.trim().toLowerCase();
            const shouldRetry =
                normalizedFallback.length > 0 &&
                normalizedFallback !== normalizedPrimary &&
                firstPass.unknownFields.length > 0;

            if (!shouldRetry) {
                // No retry — return first pass result as-is (no retryAttempted flags needed)
                return firstPass;
            }

            // Build sections containing only the fields that returned Unknown
            const unknownVariables = new Set(firstPass.unknownFields.map(f => f.variable));
            const retrySections = sections.map(s => ({
                ...s,
                templateFormFields: (s.templateFormFields ?? []).filter(
                    f => f.associatedVariable && unknownVariables.has(f.associatedVariable)
                )
            })).filter(s => s.templateFormFields.length > 0);

            // Retry pass — only for Unknown fields, using the fallback URL
            const retryPass = await resolveAiAutofillConfig(
                firstPass.config, retrySections, locationName, fallbackUrl, skipVariables
            );

            // Merge: retry successes fold into the primary result
            const retriedSuccessVars = retryPass.succeededVariables;

            const mergedConfig = retryPass.config; // retryPass starts from firstPass.config

            const mergedSucceededFields = [
                ...firstPass.succeededFields,
                ...retryPass.succeededFields,
            ];

            const mergedSucceededVariables = new Set([
                ...firstPass.succeededVariables,
                ...retryPass.succeededVariables,
            ]);

            // Failed fields: those that were Unknown on the primary AND still Unknown on retry.
            // Remove entries from firstPass.skippedWithErrors that succeeded on retry, then
            // mark the remaining ones (truly failed both passes) with retryAttempted: true.
            const mergedSkippedWithErrors = [
                // Non-Unknown failures from first pass pass through unchanged
                ...firstPass.skippedWithErrors.filter(
                    e => !firstPass.unknownFields.some(u => u.fieldLabel === e.fieldLabel)
                ),
                // Unknown failures from first pass: succeeded on retry → drop; failed retry → keep with flag
                ...firstPass.unknownFields
                    .filter(u => !retriedSuccessVars.has(u.variable))
                    .map(u => ({
                        fieldLabel: u.fieldLabel,
                        error: 'Could not retrieve a value from the website.',
                        appName: locationName || undefined,
                        retryAttempted: true as const,
                    })),
            ];

            return {
                config: mergedConfig,
                succeededFields: mergedSucceededFields,
                skippedWithErrors: mergedSkippedWithErrors,
                succeededVariables: mergedSucceededVariables,
                unknownFields: [], // consumed — no further retry
            };
        };

        props.setIsSubmittingForm(true);
        props.setProgressMode?.(commonAppWasCreated ? 'creating' : 'updating');
        props.setProgressStep?.('setup-module');

        props.setProgressLocationName?.(getCommonAppNameForAssistantType(values.assistantType));

        // Clear any previous errors
        setStatus(null);

        try {
            const aiAnsweringTemplateConfigId = getAIAnsweringModuleId();
            const foodOrderingModuleId = getFoodOrderingModuleId();
            const healthcareSchedulingModuleId = getHealthcareSchedulingModuleId();
            let commonAppId = "";
            let commonAppTemplateConfigId = "";

            // ----------------------------------------------------------------
            // Hoist shared API calls — fetched once and reused throughout the
            // entire submit/update flow instead of being re-fetched inside each
            // inner function or per-location loop.
            // ----------------------------------------------------------------
            const [availableTemplateConfigsResponse, allFeatureFlagsResponse] = await Promise.all([
                getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id),
                getFeatureFlags().catch(() => ({ data: [] as any[] })),
            ]);
            if (availableTemplateConfigsResponse.resultType !== 'Ok' || !availableTemplateConfigsResponse.data) {
                throw new Error(`Failed to load available template configurations: ${availableTemplateConfigsResponse.resultType}`);
            }
            const availableTemplateConfigsData = availableTemplateConfigsResponse.data;
            const allFeatureFlagsData: any[] = (allFeatureFlagsResponse as any).data ?? [];

            const updateTemplateFormSection = (orgTemplateConfig: TemplateConfigurationModel, coreTemplateConfig: TemplateConfigurationModel, values: ManageSparkLocationsFormData) => {

                const updatedSections: any[] = [];
                for (const commonAppSection of [...coreTemplateConfig.templateForm.templateFormSections]) {

                    if (commonAppSection.templateFormFields.some((field: any) => values.selectedFormFields.some((selectedField: any) => fieldsMatch(selectedField, field)))) {
                        let newSection = orgTemplateConfig.templateForm.templateFormSections.find((templateSection: any) => templateSection.title === commonAppSection.title);

                        if (!newSection) {
                            newSection = {
                                ...commonAppSection,
                                templateFormFields: [],
                                templateFormId: orgTemplateConfig.templateForm.id,
                                id: uuidv4()
                            };
                        } else {
                            newSection =
                            {
                                ...commonAppSection,
                                templateFormFields: newSection.templateFormFields,
                                id: newSection.id,
                                templateFormId: newSection.templateFormId,
                            }
                        };

                        const updatedFields: any[] = [];

                        for (const commonAppField of commonAppSection.templateFormFields) {
                            if (values.selectedFormFields.some((selectedField: any) => fieldsMatch(selectedField, commonAppField))) {
                                let newField = newSection.templateFormFields.find((templateField: any) => fieldsMatch(templateField, commonAppField));
                                if (!newField) {
                                    newField = {
                                        ...commonAppField,
                                        templateFormSectionId: newSection.id,
                                        id: uuidv4()
                                    };
                                } else {
                                    newField = {
                                        ...commonAppField,
                                        id: newField.id,
                                        templateFormSectionId: newSection.id,
                                    }
                                }
                                updatedFields.push(newField);
                            }
                        };

                        newSection.templateFormFields = updatedFields;
                        updatedSections.push(newSection);

                    }
                }

                return updatedSections;
            };

            const updateTemplateFormFieldValues = (formFieldValues: TemplateFormFieldValueModel[], commonAppSections: TemplateFormSectionModel[], isUpdate?: boolean, applyDefaultsForCommonApp?: boolean, aiConfig?: Record<string, string> | null) => {
                const templateValues: TemplateFormFieldValueModel[] = [];

                // Collect all fields across all sections for cross-section conditional default evaluation
                const allFields = commonAppSections.flatMap(s => s.templateFormFields ?? []);

                // Get default configuration if we're applying defaults for common app
                const defaultConfig = applyDefaultsForCommonApp
                    ? getDefaultAssistantConfig(values.assistantType, values.pmsName || values.posNameOrdering, '', '', '')
                    : null;

                // Maintain an incremental value map so conditional default evaluation
                // never needs to rescan the accumulator array — O(N) for the whole loop.
                const incrementalValueMap: Record<string, string | undefined> = {};

                for (const section of commonAppSections) {

                    section.templateFormFields.forEach((formField) => {

                        let formFieldValueModel = findFieldValueForField(formField, formFieldValues);
                        let formFieldValue = formField.defaultValue;

                        if (formFieldValueModel) {
                            formFieldValue = formFieldValueModel.value;
                        };

                        if (values.selectedFormFields.some(sf => fieldsMatch(sf, formField)) && formField.associatedVariable) {
                            formFieldValue = formField.associatedVariable;
                        };

                        // Apply default config values for fields NOT selected as location-specific
                        if (applyDefaultsForCommonApp && defaultConfig) {
                            const isFieldSelected = values.selectedFormFields.some(
                                sf => fieldsMatch(sf, formField)
                            );

                            // Look up by associatedVariable first (stable identifier), fall back to title
                            const configKey = formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                                ? formField.associatedVariable
                                : formField.title;

                            // Only apply to non-selected fields
                            if (!isFieldSelected && defaultConfig[configKey] !== undefined) {
                                const configValue = defaultConfig[configKey];
                                if (configValue !== '' && configValue !== null && configValue !== undefined) {
                                    formFieldValue = typeof configValue === 'string' ? configValue : String(configValue);
                                }
                            }
                        }

                        // Apply AI-generated values for non-selected fields whose associatedVariable
                        // is in the autofill config. AI values fill in empty/missing slots that static
                        // defaults leave blank. "Unknown" responses were already filtered out in
                        // resolveAiAutofillConfig so anything present here is valid content.
                        if (aiConfig && formField.associatedVariable) {
                            const isFieldSelected = values.selectedFormFields.some(
                                sf => fieldsMatch(sf, formField)
                            );
                            if (!isFieldSelected) {
                                const aiValue = aiConfig[formField.associatedVariable];
                                if (aiValue !== undefined && aiValue !== '') {
                                    formFieldValue = aiValue;
                                }
                            }
                        }

                        if (isUpdate) {
                            if (values.deselectedFormFields.some(df => fieldsMatch(df, formField))) {
                                if (formField.defaultValue) {
                                    formFieldValue = formField.defaultValue;
                                } else {
                                    formFieldValue = undefined;
                                }
                            };
                        };

                        // If we still only have the schema default, try evaluating conditional defaults
                        // using the incrementally-maintained map — no array rescan needed per field.
                        if (formFieldValue === formField.defaultValue || formFieldValue === undefined) {
                            const conditionalDefault = evaluateConditionalDefault(formField, allFields, templateValues, incrementalValueMap);
                            if (conditionalDefault !== undefined) {
                                formFieldValue = conditionalDefault;
                            }
                        }

                        const fieldValue = {
                            value: formFieldValue,
                            fieldType: formField.fieldType,
                            tip: formField.tip,
                            title: formField.title,
                            label: formField.label,
                            defaultValue: formField.defaultValue,
                            associatedVariable: formField.associatedVariable,
                            placeholder: formField.placeholder,
                            templateFormFieldId: formField.id,
                            dynamicFormFieldName: formField.name
                        };

                        templateValues.push(fieldValue);

                        // Keep the incremental map current so subsequent fields can reference this one.
                        if (formField.associatedVariable) {
                            incrementalValueMap[formField.associatedVariable] = formFieldValue;
                        }

                    });
                }

                return templateValues;

            };

            const createCommonAppAndAppliedAppTemplate = async () => {

                // Get the appropriate common app name based on assistant type
                const getCommonAppName = (assistantType: string) => {
                    switch (assistantType) {
                        case 'ai-answering':
                            return aiAnsweringCommonAppName;
                        case 'ai-ordering':
                            return aiOrderingCommonAppName;
                        case 'dental-scheduling':
                            return aiDentalSchedulingCommonAppName;
                        default:
                            return aiAnsweringCommonAppName;
                    }
                };

                const commonAppName = getCommonAppName(values.assistantType);

                // create common app
                const createCommonAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                    name: commonAppName,
                    shortDescription: commonAppName,
                    description: commonAppName,
                    invocationPhrase: commonAppName
                });

                if (createCommonAppResponse.resultType !== 'Ok' || !createCommonAppResponse.data?.id) {
                    throw new Error(`Failed to create shared configuration app: ${createCommonAppResponse.errors?.join(', ') || createCommonAppResponse.resultType}`);
                }

                commonAppId = createCommonAppResponse.data.id

                // update common app to allow templating
                await toggleAllowsTemplating(createCommonAppResponse.data.id, true);

                // Get the selected module ID based on assistant type
                const selectedModuleId = getSelectedModuleId(values.assistantType);

                // add applied app template referencing the selected template configuration to common app
                const applySelectedTemplateToCommonAppResponse = await addAppliedApplicationTemplate(commonAppId, selectedModuleId);

                if (applySelectedTemplateToCommonAppResponse.resultType !== 'Ok' || !applySelectedTemplateToCommonAppResponse.data) {
                    throw new Error(`Failed to apply template to shared configuration app: ${applySelectedTemplateToCommonAppResponse.errors?.join(', ') || applySelectedTemplateToCommonAppResponse.resultType}`);
                }

                // Get the template sections for the selected assistant type (uses hoisted data)
                const selectedModuleConfig = availableTemplateConfigsData.find(config => config.id === selectedModuleId);
                const selectedModuleSections = selectedModuleConfig ? selectedModuleConfig.templateForm.templateFormSections : [];

                // AI autofill for shared config — uses org-level websiteUrl.
                // Only generate values for fields that are NOT location-specific (i.e. not in
                // selectedFormFields). Location-specific fields must be generated per-location
                // using each location's URL, not the org-level URL.
                // Only runs when at least one location has autogenerate enabled.
                // resolveAiAutofillConfig is non-fatal; returns {} on any error or missing inputs.
                const anyLocationAutogenerateOnCreate = Object.values(values.locationAutogenerateEnabled ?? {}).some(Boolean);
                const sharedOnlySections = selectedModuleSections.map(section => ({
                    ...section,
                    templateFormFields: (section.templateFormFields ?? []).filter(
                        field => !values.selectedFormFields.some(sf => fieldsMatch(sf, field))
                    )
                })).filter(section => section.templateFormFields.length > 0);
                let commonAppAiConfig: Record<string, string> = {};
                if (anyLocationAutogenerateOnCreate) {
                    // Use the first non-empty location URL as the fallback for shared-config
                    // fields that return Unknown on the primary org URL.  If the content is
                    // only present on a location page, the retry may recover it.
                    const firstLocationUrl = Object.values(values.locationWebsiteUrls ?? {})
                        .map(u => (u ?? '').trim())
                        .find(u => u.length > 0) ?? '';
                    const commonAppAutofillResult = await resolveAiAutofillConfigWithRetry(
                        {},
                        sharedOnlySections,
                        commonAppName,
                        values.websiteUrl ?? '',   // primary: org URL
                        firstLocationUrl,           // fallback: first location URL
                        new Set(),
                        'toLocationSpecific'
                    );
                    commonAppAiConfig = commonAppAutofillResult.config;
                    allAutofillErrors.push(...commonAppAutofillResult.skippedWithErrors);
                    allAutofillSuccesses.push(...commonAppAutofillResult.succeededFields);
                }
                // Record only the non-location-specific variables as covered by the shared call
                // so that per-location calls skip them. Location-specific variables must NOT be
                // added here — they need to be generated per-location with each location's URL.
                const sharedSectionFields = sharedOnlySections.flatMap(s => s.templateFormFields ?? []);
                const allowlistForType = FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE[values.assistantType];
                if (allowlistForType) {
                    for (const variable of allowlistForType) {
                        const coveredByShared = sharedSectionFields.some(f => f.associatedVariable === variable);
                        if (coveredByShared) sharedAutofillVariables.add(variable);
                    }
                }

                // {OfficeWebsite} — populate directly from org-level URL for dental scheduling.
                // Only set when websiteUrl is non-empty; do not overwrite with blank.
                if (values.assistantType === 'dental-scheduling' && values.websiteUrl) {
                    commonAppAiConfig['{OfficeWebsite}'] = values.websiteUrl;
                }

                // update the common app applied application template with the latest template form field values
                const updatedCommonAppAppliedTemplateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                    templateFormFieldValues: updateTemplateFormFieldValues(applySelectedTemplateToCommonAppResponse.data.templateFormFieldValues, selectedModuleSections, false, true, commonAppAiConfig),
                    dynamicFormSections: applySelectedTemplateToCommonAppResponse.data.dynamicFormSections,
                    name: applySelectedTemplateToCommonAppResponse.data.name,
                };

                const commonAppAppliedAppId = applySelectedTemplateToCommonAppResponse.data.id;

                await updateAppliedApplicationTemplate(commonAppAppliedAppId, updatedCommonAppAppliedTemplateRequest);

                const updateApplicationRequest: UpdateApplicationRequest = {
                    name: createCommonAppResponse.data.name,
                    shortDescription: createCommonAppResponse.data.shortDescription,
                    description: createCommonAppResponse.data.description,
                    keywords: createCommonAppResponse.data.keywords,
                    invocationPhrase: createCommonAppResponse.data.invocationPhrase,
                    imageItemId: createCommonAppResponse.data.imageItemId,
                    featureFlags: [{ id: getFeatureFlagForAssistantType(values.assistantType) }],
                    ...(values.assistantType === 'dental-scheduling' && {
                        pmsName: values.pmsName
                    })
                };

                await updateApplication(commonAppId, updateApplicationRequest);

            };

            const updateCommonAppAndAppliedAppTemplate = async () => {

                commonAppId = props.commonApp.id;

                // Get the template sections for the selected assistant type (uses hoisted data)
                const selectedModuleId = getSelectedModuleId(values.assistantType);
                const selectedModuleConfig = availableTemplateConfigsData.find(config => config.id === selectedModuleId);
                const selectedModuleSections = selectedModuleConfig ? selectedModuleConfig.templateForm.templateFormSections : [];

                // AI autofill for shared config on update.
                // Only generate values for non-location-specific fields (fields NOT in
                // selectedFormFields). Location-specific fields are generated per-location below.
                //
                // Two independent triggers:
                //   1. Original behaviour (unchanged): at least one location has autogenerate
                //      enabled AND a website URL is provided. sharedAutofillVariables is populated
                //      so the per-location calls below skip those variables.
                //   2. New: no location has autogenerate enabled but a website URL is provided.
                //      Shared config fields are autofilled in isolation; no per-location calls
                //      follow so sharedAutofillVariables does not need to be populated.
                let commonAppAiConfig: Record<string, string> = {};
                if (values.websiteUrl) {
                    const sharedOnlySections = selectedModuleSections.map(section => ({
                        ...section,
                        templateFormFields: (section.templateFormFields ?? []).filter(
                            field => !values.selectedFormFields.some(sf => fieldsMatch(sf, field))
                        )
                    })).filter(section => section.templateFormFields.length > 0);
                    const commonAppAutofillResult = await resolveAiAutofillConfig(
                        {},
                        sharedOnlySections,
                        props.commonApp?.name ?? '',
                        values.websiteUrl ?? ''
                    );
                    commonAppAiConfig = commonAppAutofillResult.config;
                    allAutofillErrors.push(...commonAppAutofillResult.skippedWithErrors);
                    allAutofillSuccesses.push(...commonAppAutofillResult.succeededFields);
                    // Record only non-location-specific variables as covered by the shared call so
                    // that per-location calls skip them. Location-specific variables must NOT be
                    // added here — they need to be generated per-location with each location's URL.
                    const sharedSectionFields = sharedOnlySections.flatMap(s => s.templateFormFields ?? []);
                    const allowlistForType = FORM_FILL_VARIABLES_BY_ASSISTANT_TYPE[values.assistantType];
                    if (allowlistForType) {
                        for (const variable of allowlistForType) {
                            const coveredByShared = sharedSectionFields.some(f => f.associatedVariable === variable);
                            if (coveredByShared) sharedAutofillVariables.add(variable);
                        }
                    }
                }

                // update the common app applied application template with the latest configuration
                const appliedAppTemplatesResponse = await getAppliedApplicationTemplatesForApp(commonAppId);
                if (appliedAppTemplatesResponse.resultType !== 'Ok' || !appliedAppTemplatesResponse.data) {
                    throw new Error(`Failed to load applied templates for shared configuration app: ${appliedAppTemplatesResponse.resultType}`);
                }
                for (let i = 0; i < appliedAppTemplatesResponse.data?.length; i++) {
                    const appliedApplicationTemplateIndex = i;
                    const commonAppAppliedAppTemplate = appliedAppTemplatesResponse.data[appliedApplicationTemplateIndex]
                    const commonAppAppliedAppTemplateId = commonAppAppliedAppTemplate.id;

                    // Capture the real field values from the shared config BEFORE sentinel strings
                    // are stamped over them for newly-selected (location-specific) fields.
                    // updateExistingLocationApp reads these to seed location apps that have no
                    // prior value for a field that just became location-specific.
                    sharedConfigPreStampValues = [
                        ...sharedConfigPreStampValues,
                        ...(commonAppAppliedAppTemplate.templateFormFieldValues ?? [])
                    ];

                    const updateAppliedAppTemplateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                        templateFormFieldValues: updateTemplateFormFieldValues(commonAppAppliedAppTemplate.templateFormFieldValues, selectedModuleSections, true, false, commonAppAiConfig),
                        dynamicFormSections: appliedAppTemplatesResponse.data[appliedApplicationTemplateIndex].dynamicFormSections,
                        name: appliedAppTemplatesResponse.data[appliedApplicationTemplateIndex].name,
                    };

                    await updateAppliedApplicationTemplate(commonAppAppliedAppTemplateId, updateAppliedAppTemplateRequest);

                }
            };

            const createCommonAppTemplateConfiguration = async () => {

                const commonAppTemplateFormId = uuidv4();
                const customModuleName = getCustomModuleName(values.assistantType);

                // Get the template sections for the selected assistant type (uses hoisted data)
                const availableTemplateConfigsResponse = { data: availableTemplateConfigsData };
                const selectedModuleId = getSelectedModuleId(values.assistantType);
                const selectedModuleConfig = availableTemplateConfigsData.find(config => config.id === selectedModuleId);

                if (!selectedModuleConfig) {
                    throw new Error(`Template configuration not found for assistant type "${values.assistantType}". This module may not be configured yet. Please contact support if you expected this module to be available.`);
                }

                const selectedModuleSections = selectedModuleConfig.templateForm.templateFormSections;

                const commonAppTemplateConfigurationRequest: TemplateConfigurationModel = {
                    name: customModuleName,
                    description: `${props.orgContainer.state.currentOrganization.name} ${customModuleName}`,
                    organizationId: props.orgContainer.state.currentOrganization.id,
                    templateApplicationId: commonAppId,
                    finalizeFormUrl: null,
                    templateForm: {
                        id: commonAppTemplateFormId,
                        templateFormSections: selectedModuleSections.filter(section => {
                            section.templateFormFields = section.templateFormFields.filter(field => {
                                return values.selectedFormFields.some(selectedField =>
                                    fieldsMatch(selectedField, field)
                                );
                            });
                            return section.templateFormFields.length > 0;
                        })
                    },
                    currentVersion: { "versionName": "1.0", "releaseNotes": "initial template" }
                };

                for (const commonAppTemplateFormSection of commonAppTemplateConfigurationRequest.templateForm.templateFormSections) {
                    commonAppTemplateFormSection.templateFormId = commonAppTemplateFormId;
                    commonAppTemplateFormSection.id = uuidv4();
                    for (const commonAppTemplateFormField of commonAppTemplateFormSection.templateFormFields) {
                        commonAppTemplateFormField.id = uuidv4();
                        commonAppTemplateFormField.templateFormSectionId = commonAppTemplateFormSection.id;
                    }
                }

                const createTemplateConfigurationResponse = await createTemplateConfiguration(commonAppTemplateConfigurationRequest);
                commonAppTemplateConfigId = createTemplateConfigurationResponse.data.id;

            };

            const updateCommonAppTemplateConfiguration = async () => {

                commonAppTemplateConfigId = orgTemplateConfig.id;

                // Get the selected module ID based on assistant type (uses hoisted data)
                const selectedModuleId = getSelectedModuleId(values.assistantType);

                const coreTemplateConfig = availableTemplateConfigsData.find(templateConfig => templateConfig.id === selectedModuleId);

                // Ensure we have a valid core template config before proceeding
                if (!coreTemplateConfig) {
                    throw new Error(`Template configuration not found for assistant type "${values.assistantType}". This module may not be configured yet. Please contact support if you expected this module to be available.`);
                }

                // No longer automatically merging all modules - only use the selected one
                const commonAppUpdatedTemplateForm = {
                    ...orgTemplateConfig.templateForm,
                    templateFormSections: updateTemplateFormSection(orgTemplateConfig, coreTemplateConfig, values)
                }

                const commonAppUpdatedTemplateConfiguration: TemplateConfigurationModel = {
                    ...orgTemplateConfig,
                    templateForm: commonAppUpdatedTemplateForm
                }

                // update common app template configuration with latest form from selected module
                await updateTemplateConfiguration(commonAppTemplateConfigId, commonAppUpdatedTemplateConfiguration);
            };

            const createLocationAppAndApplyAppTemplate = async (locationName: string, locationIndex: number, orgTemplateConfigForLocation: any) => {

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'creating-app' as LocationSubStep }));

                const createLocationAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                    name: locationName,
                    shortDescription: toShortDescription(locationName),
                    description: locationName,
                    invocationPhrase: locationName
                });
                if (createLocationAppResponse.resultType !== 'Ok' || !createLocationAppResponse.data?.id) {
                    throw new Error(`Failed to create location app for "${locationName}": ${createLocationAppResponse.errors?.join(', ') || createLocationAppResponse.resultType}`);
                }

                const locationAppId = createLocationAppResponse.data.id;
                const locationAppSecret = createLocationAppResponse.data.secret ?? "";
                const locationAppFullName = locationName;

                // Fetch all feature flags once — shared by Manage Menu and location app GenAI steps
                // (uses hoisted feature flags data instead of a per-location network call)
                let genAIFoodOrderingFlag: any = undefined;
                if (values.assistantType === 'ai-ordering') {
                    genAIFoodOrderingFlag = allFeatureFlagsData.find(f => f.id === GenAIFoodOrdering);
                }

                // ----------------------------------------------------------------
                // AI Ordering: create Manage Menu app + apply GenAI settings
                // ----------------------------------------------------------------
                let manageMenuAppId: string | undefined;
                if (values.assistantType === 'ai-ordering') {
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'create-manage-menu' as LocationSubStep }));
                    const storeId = values.storeIds?.[locationIndex]?.trim() ?? '';
                    const manageMenuAppName = `${locationAppFullName} - Manage Menu`;

                    // Step A-1: Create Manage Menu app
                    const manageMenuAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                        name: manageMenuAppName,
                        shortDescription: toShortDescription(manageMenuAppName),
                        description: manageMenuAppName,
                        invocationPhrase: manageMenuAppName
                    });
                    if (manageMenuAppResponse.resultType !== 'Ok' || !manageMenuAppResponse.data?.id) {
                        throw new Error(`Failed to create Manage Menu app for "${locationName}": ${manageMenuAppResponse.errors?.join(', ') || manageMenuAppResponse.resultType}`);
                    }
                    manageMenuAppId = manageMenuAppResponse.data.id;

                    // Step A-2: Apply Food Ordering flag + POS settings to Manage Menu app
                    const manageMenuUpdateRequest: UpdateApplicationRequest = {
                        name: manageMenuAppResponse.data.name,
                        shortDescription: manageMenuAppResponse.data.shortDescription,
                        description: manageMenuAppResponse.data.description,
                        keywords: manageMenuAppResponse.data.keywords,
                        invocationPhrase: manageMenuAppResponse.data.invocationPhrase,
                        imageItemId: manageMenuAppResponse.data.imageItemId,
                        featureFlags: [{ id: FoodOrderingFeatureFlagId }],
                        posName: values.posNameOrdering,
                        posLocationId: storeId
                    };
                    await updateApplication(manageMenuAppId, manageMenuUpdateRequest);

                    // Step A-3: Apply GenAI settings to Manage Menu app (non-fatal if flag not found)
                    if (genAIFoodOrderingFlag) {
                        await updateApplicationFeatureFlagSettings(manageMenuAppId, {
                            featureFlagId: GenAIFoodOrdering,
                            featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                        });
                    }
                }

                // ----------------------------------------------------------------
                // Standard location app update
                // ----------------------------------------------------------------
                const storeId = values.assistantType === 'ai-ordering'
                    ? (values.storeIds?.[locationIndex] ?? '')
                    : undefined;

                const locationAppFeatureFlags = values.assistantType === 'ai-ordering'
                    ? [{ id: FoodOrderingFeatureFlagId }, { id: GenAIFoodOrdering }, { id: DisableLegacyCheckoutFlowFeatureFlagId }]
                    : [{ id: getFeatureFlagForAssistantType(values.assistantType) }];

                const updateApplicationRequest: UpdateApplicationRequest = {
                    name: createLocationAppResponse.data.name,
                    shortDescription: createLocationAppResponse.data.shortDescription,
                    description: createLocationAppResponse.data.description,
                    keywords: createLocationAppResponse.data.keywords,
                    invocationPhrase: createLocationAppResponse.data.invocationPhrase,
                    imageItemId: createLocationAppResponse.data.imageItemId,
                    featureFlags: locationAppFeatureFlags,
                    ...(values.assistantType === 'ai-ordering' && {
                        posName: values.posNameOrdering,
                        posLocationId: storeId,
                        manageMenuApplicationId: manageMenuAppId
                    }),
                    ...(values.assistantType === 'dental-scheduling' && {
                        pmsName: values.pmsName,
                        pmsLocationId: values.storeIds?.[locationIndex] ?? undefined,
                    })
                };

                await updateApplication(locationAppId, updateApplicationRequest);

                // POS configuration is provisioned only for newly-created AI Ordering locations.
                // Never overwrite an existing config, even though the application itself was just
                // created: the POS service is the authority for whether this location already has one.
                if (values.assistantType === 'ai-ordering' && !values.skipPOSConfigLocationIndexes?.includes(locationIndex)) {
                    const isSandboxLocation = isSandboxConfiguring(values.assistantSetupType, storeId, values.posNameOrdering);
                    try {
                        const existingConfigResult = await loadPOSConfigForLocationId(locationAppId);
                        const hasExistingConfig = hasExistingPOSConfig(existingConfigResult);

                        if (hasExistingConfig) {
                            if (!isSandboxLocation) {
                                posConfigErrors.push(`POS configuration was not created for "${locationName}" because this location already has a POS configuration.`);
                            }
                        } else if (!canCreatePOSConfig(existingConfigResult)) {
                            if (!isSandboxLocation) {
                                posConfigErrors.push(`POS configuration was not created for "${locationName}" because the existing configuration could not be checked: ${existingConfigResult.errors?.join(', ') || existingConfigResult.resultType}.`);
                            }
                        } else if (!locationAppSecret) {
                            if (!isSandboxLocation) {
                                posConfigErrors.push(`POS configuration was not created for "${locationName}" because the new application's secret was unavailable.`);
                            }
                        } else {
                            const cloneEnabled = !!values.locationCloneEnabled?.[locationIndex];
                            const cloneSourceAppId = values.locationCloneSourceAppIds?.[locationIndex];
                            let sourceConfig: string | undefined;

                            if (cloneEnabled && cloneSourceAppId) {
                                const sourceConfigResult = await getPOSConfig(cloneSourceAppId);
                                if (sourceConfigResult.resultType !== 'Ok' || !sourceConfigResult.data?.data?.trim()) {
                                    if (!isSandboxLocation) {
                                        posConfigErrors.push(`POS configuration was not created for "${locationName}" because the clone source location does not have a POS configuration.`);
                                    }
                                } else {
                                    sourceConfig = sourceConfigResult.data.data;
                                }
                            }

                            if (!cloneEnabled || sourceConfig) {
                                const newConfig = buildNewLocationPOSConfig(locationAppId, locationAppSecret, sourceConfig, storeId);
                                const updateConfigResult = await updatePOSConfig(locationAppId, newConfig);
                                if (updateConfigResult.resultType !== 'Ok') {
                                    if (!isSandboxLocation) {
                                        posConfigErrors.push(`POS configuration could not be created for "${locationName}": ${updateConfigResult.errors?.join(', ') || updateConfigResult.resultType}.`);
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        if (!isSandboxLocation) {
                            const reason = error instanceof Error ? error.message : 'An unexpected error occurred.';
                            posConfigErrors.push(`POS configuration could not be created for "${locationName}": ${reason}`);
                        }
                    }
                }

                if (values.assistantType === 'ai-ordering') {
                    const environmentsResponse = await getApplicationEnvironments(locationAppId);
                    const productionEnv = environmentsResponse.data?.find((env: any) => env.name === 'Production');
                    if (productionEnv) {
                        const existingFlags: any[] = productionEnv.featureFlags ?? [];
                        const hasDisableLegacy = existingFlags.some((f: any) => f.id === DisableLegacyCheckoutFlowFeatureFlagId);
                        if (!hasDisableLegacy) {
                            await updateEnvironmentSettings(productionEnv.id, {
                                name: productionEnv.name,
                                applicationName: createLocationAppResponse.data.name,
                                featureFlags: [
                                    ...existingFlags,
                                    { id: DisableLegacyCheckoutFlowFeatureFlagId }
                                ]
                            });
                        }
                    }
                }

                // Apply GenAI settings to location app (non-fatal if flag not found)
                if (values.assistantType === 'ai-ordering' && genAIFoodOrderingFlag) {
                    await updateApplicationFeatureFlagSettings(locationAppId, {
                        featureFlagId: GenAIFoodOrdering,
                        featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                    });
                }

                // Enable detailed analytics for the new app
                await props.appContainer.updateDetailedAnalytics(locationAppId, true);

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'applying-template' as LocationSubStep }));

                // Apply the template configuration to the location app
                const appliedTemplateResponse = await addAppliedApplicationTemplate(locationAppId, commonAppTemplateConfigId);

                // Get the org-scoped template configuration to access the location-specific form fields
                // (hoisted from the outer scope — already resolved before the location loop)

                // Get default configuration for this assistant type
                const defaultConfig = getDefaultAssistantConfig(
                    values.assistantType,
                    values.pmsName || values.posNameOrdering,
                    locationName,
                    locationAppId,
                    locationAppSecret,
                    values.storeIds?.[locationIndex]
                );

                // AI autofill — initial creation only (!props.commonApp is checked at call site).
                // Per-location URL falls back to org-level URL when not specified.
                // sharedAutofillVariables ensures variables already fetched for the common app
                // are not called a second time.
                // Only run autofill when this specific location has autogenerate enabled.
                const locationAutofillEnabled = !!(values.locationAutogenerateEnabled?.[locationIndex]);
                const cloneEnabled = !!(values.locationCloneEnabled?.[locationIndex]);
                const cloneSourceAppId = values.locationCloneSourceAppIds?.[locationIndex];
                let cloneFieldValues: TemplateFormFieldValueModel[] = [];

                if (cloneEnabled && cloneSourceAppId) {
                    const sourceAppliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(cloneSourceAppId);
                    if (sourceAppliedTemplatesResponse.resultType !== 'Ok' || !sourceAppliedTemplatesResponse.data) {
                        throw new Error(
                            `Failed to load source app applied templates for cloning (appId: ${cloneSourceAppId}).`
                        );
                    }

                    const sourceManagedTemplate = getManagedLocationAppliedTemplate(
                        sourceAppliedTemplatesResponse.data,
                        props.orgContainer.state.currentOrganization.id,
                        commonAppTemplateConfigId
                    );

                    if (!sourceManagedTemplate) {
                        throw new Error(
                            `No managed template found for source location app while cloning (appId: ${cloneSourceAppId}).`
                        );
                    }

                    cloneFieldValues = sourceManagedTemplate.templateFormFieldValues ?? [];
                }

                const locationUrl = values.locationWebsiteUrls?.[locationIndex] || values.websiteUrl || '';
                const alwaysAutogenerateOrderingCloneFields = values.assistantType === 'ai-ordering' && cloneEnabled;
                const generateOrderingCloneAddress = needsOrderingCloneAddressGeneration(values.assistantType, cloneEnabled);
                const sectionsToAutofill = cloneEnabled
                    ? (orgTemplateConfigForLocation?.templateForm.templateFormSections ?? []).map(section => ({
                        ...section,
                        templateFormFields: (section.templateFormFields ?? []).filter(field => {
                            const isAlwaysAutogenerated = generateOrderingCloneAddress
                                && isStoreAddressField(field);
                            const isNormallyAutogenerated = locationAutofillEnabled
                                && ((field.associatedVariable && CLONE_AUTOGENERATE_ALLOWLIST.has(field.associatedVariable))
                                    || CLONE_AUTOGENERATE_TITLES_ALLOWLIST.has(field.title));
                            const isDefaultOnly = (field.associatedVariable && CLONE_USE_DEFAULT_ALLOWLIST.has(field.associatedVariable))
                                || CLONE_USE_DEFAULT_TITLES_ALLOWLIST.has(field.title);
                            return (isAlwaysAutogenerated || isNormallyAutogenerated) && !isDefaultOnly;
                        }).map(field => ({
                            ...field,
                            // Title-only ordering addresses use the same API variable and retry path.
                            associatedVariable: getLocationAutofillVariable(field, values.assistantType)
                        }))
                    })).filter(section => section.templateFormFields.length > 0)
                    : (orgTemplateConfigForLocation?.templateForm.templateFormSections ?? []);

                const locationAutofillResult = (locationAutofillEnabled || generateOrderingCloneAddress)
                    ? await resolveAiAutofillConfigWithRetry(
                        defaultConfig,
                        sectionsToAutofill,
                        locationName,
                        locationUrl,              // primary: location URL
                        values.websiteUrl ?? '', // fallback: org URL
                        sharedAutofillVariables,
                        'toSharedConfig'
                    )
                    : { config: defaultConfig, succeededFields: [], skippedWithErrors: [], succeededVariables: new Set<string>(), unknownFields: [] };
                const mergedConfig = locationAutofillResult.config;
                allAutofillErrors.push(...locationAutofillResult.skippedWithErrors);
                allAutofillSuccesses.push(...locationAutofillResult.succeededFields);

                // {OfficeWebsite} — populate directly from org-level URL for dental scheduling.
                // Only set when websiteUrl is non-empty; do not overwrite with blank.
                if (values.assistantType === 'dental-scheduling' && values.websiteUrl) {
                    mergedConfig['{OfficeWebsite}'] = values.websiteUrl;
                }

                // Build template field values from the template configuration sections
                const templateFieldValues: TemplateFormFieldValueModel[] = [];
                // Incremental map: updated after each field so conditional default evaluation
                // never re-scans the accumulator — O(N) for the whole loop.
                const incrementalLocationValueMap: Record<string, string | undefined> = {};

                if (orgTemplateConfigForLocation) {
                    const allLocationFields = orgTemplateConfigForLocation.templateForm.templateFormSections.flatMap(s => s.templateFormFields ?? []);
                    for (const section of orgTemplateConfigForLocation.templateForm.templateFormSections) {
                        for (const formField of section.templateFormFields) {
                            const fieldTitle = formField.title;
                            let fieldValue = formField.defaultValue;

                            // Check if this field is selected as location-specific
                            const isFieldSelected = values.selectedFormFields.some(
                                selectedField => fieldsMatch(selectedField, formField)
                            );

                            const isCloneFieldAllowlisted = (formField.associatedVariable && CLONE_AUTOGENERATE_ALLOWLIST.has(formField.associatedVariable))
                                || CLONE_AUTOGENERATE_TITLES_ALLOWLIST.has(formField.title);

                            const isCloneFieldDefaultOnly = (formField.associatedVariable && CLONE_USE_DEFAULT_ALLOWLIST.has(formField.associatedVariable))
                                || CLONE_USE_DEFAULT_TITLES_ALLOWLIST.has(formField.title);

                            const isAlwaysAutogeneratedOrderingCloneField = alwaysAutogenerateOrderingCloneFields
                                && isStoreAddressField(formField);

                            const clonedFieldValueModel = (cloneEnabled && !isCloneFieldDefaultOnly && !isAlwaysAutogeneratedOrderingCloneField)
                                ? findFieldValueForField(formField, cloneFieldValues)
                                : undefined;

                            if (clonedFieldValueModel && (!locationAutofillEnabled || !isCloneFieldAllowlisted)) {
                                fieldValue = clonedFieldValueModel.value;
                            } else {
                                // Look up by associatedVariable first (stable identifier), fall back to title
                                const autofillVariable = getLocationAutofillVariable(formField, values.assistantType);
                                const configKey = autofillVariable && mergedConfig[autofillVariable] !== undefined
                                    ? autofillVariable
                                    : fieldTitle;

                                // Apply merged config value (static defaults + AI values) if field is selected
                                if (isFieldSelected && mergedConfig[configKey] !== undefined) {
                                    const configValue = mergedConfig[configKey];
                                    if (configValue !== '' && configValue !== null && configValue !== undefined) {
                                        fieldValue = typeof configValue === 'string' ? configValue : String(configValue);
                                    }
                                }

                                // Apply AI-generated value for non-selected fields that are in the autofill set.
                                // These fields have no static default but can be populated by the AI.
                                if (!isFieldSelected && autofillVariable && mergedConfig[autofillVariable] !== undefined) {
                                    const aiValue = mergedConfig[autofillVariable];
                                    if (aiValue !== '' && aiValue !== null && aiValue !== undefined) {
                                        fieldValue = typeof aiValue === 'string' ? aiValue : String(aiValue);
                                    }
                                }
                            }

                            // Fall back to conditional default if we only have the schema default.
                            // Use the incremental map — no array rescan per field.
                            if (fieldValue === formField.defaultValue || fieldValue === undefined) {
                                const conditionalDefault = evaluateConditionalDefault(formField, allLocationFields, templateFieldValues, incrementalLocationValueMap);
                                if (conditionalDefault !== undefined) {
                                    fieldValue = conditionalDefault;
                                }
                            }

                            // Create the field value model
                            templateFieldValues.push({
                                value: fieldValue,
                                fieldType: formField.fieldType,
                                tip: formField.tip,
                                title: formField.title,
                                label: formField.label,
                                defaultValue: formField.defaultValue,
                                associatedVariable: formField.associatedVariable,
                                placeholder: formField.placeholder,
                                templateFormFieldId: formField.id,
                                dynamicFormFieldName: formField.name
                            });

                            // Keep the incremental map current.
                            if (formField.associatedVariable) {
                                incrementalLocationValueMap[formField.associatedVariable] = fieldValue;
                            }
                        }
                    }
                }

                // Update the applied template with location-specific values
                const updateLocationAppTemplateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                    templateFormFieldValues: templateFieldValues,
                    dynamicFormSections: appliedTemplateResponse.data.dynamicFormSections,
                    name: appliedTemplateResponse.data.name,
                };

                await updateAppliedApplicationTemplate(appliedTemplateResponse.data.id, updateLocationAppTemplateRequest);

                // ----------------------------------------------------------------
                // AI Ordering: Pull Latest Menu from POS, then Train and Publish
                // ----------------------------------------------------------------
                if (values.assistantType === 'ai-ordering' && manageMenuAppId) {
                    const locationStoreId = values.storeIds?.[locationIndex] ?? '';

                    // Step 1: Pull Latest Menu from POS (updates the manage menu app only)
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'pull-menu' as LocationSubStep }));
                    const pullMenuRequest: UpdateMenuRequest = {
                        locationId: locationStoreId,
                        posName: values.posNameOrdering as POSNames,
                        manageMenuApplicationId: manageMenuAppId,
                        liveApplicationId: locationAppId,
                        updateLiveApplication: false,
                        skipEntityExapnsion: true,
                        skipMenuAugmentation: true,
                        orgId: props.orgContainer.state.currentOrganization.id,
                    };
                    await initiateMenuUpdate(pullMenuRequest);

                    // Step 2: Train and Publish (moves active items from manage menu app to location/assistant app)
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'sync-module' as LocationSubStep }));
                    const syncProcessId = uuidv4();
                    const syncMenuRequest: SyncMenusRequest = {
                        menuConfiguration: {
                            primaryLocation: {
                                manageMenuApplicationId: manageMenuAppId,
                                manageMenuApplicationName: `${locationAppFullName} - Manage Menu`,
                                liveApplicationId: locationAppId,
                                liveApplicationName: locationAppFullName,
                            }
                        },
                        orgId: props.orgContainer.state.currentOrganization.id,
                        processId: syncProcessId,
                    };
                    await syncMenus(syncMenuRequest);
                }

                if (values.locationCreatePhoneNumber?.[locationIndex]) {
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'creating-phone' as LocationSubStep }));
                    try {
                        if (values.locationCloneEnabled?.[locationIndex] && !values.locationCloneSourceAppIds?.[locationIndex]) {
                            throw new Error('Select a clone source to copy its phone settings.');
                        }
                        const phoneNumber = await provisionLocationPhone({
                            applicationId: locationAppId,
                            organizationName: props.orgContainer.state.currentOrganization.name,
                            locationName,
                            assistantType: values.assistantType,
                            sourceApplicationId: values.locationCloneEnabled?.[locationIndex]
                                ? values.locationCloneSourceAppIds?.[locationIndex] : undefined,
                            onWarning: warning => phoneProvisioningErrors.push(`${locationName}: ${warning}`),
                        });
                        provisionedPhoneNumbers.push(`${locationName}: ${phoneNumber}`);
                    } catch (error) {
                        const reason = error instanceof Error ? error.message : 'An unexpected error occurred.';
                        phoneProvisioningErrors.push(`Phone setup for "${locationName}" could not be completed: ${reason} The location was created; finish phone setup from its dashboard.`);
                    }
                }

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
            };

            // ----------------------------------------------------------------
            // Helper: fully update an existing location app (mirrors create path)
            // ----------------------------------------------------------------
            const updateExistingLocationApp = async (existingApp: any, locationName: string, locationIndex: number, orgTemplateConfigForLocation: any) => {

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'updating-app' as LocationSubStep }));

                const locationAppId: string = existingApp.id;
                const locationAppFullName: string = locationName;

                const appliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(locationAppId);
                if (appliedTemplatesResponse.resultType !== 'Ok') {
                    throw new Error(`Could not load the template configuration for "${locationName}". The server returned: ${appliedTemplatesResponse.errors?.join(', ') || appliedTemplatesResponse.resultType}. No changes were saved for this location.`);
                }
                if (hasDirectCoreAppliedTemplate(appliedTemplatesResponse.data ?? [], [aiAnsweringTemplateConfigId, healthcareSchedulingModuleId])) {
                    console.warn(`Skipping update for "${locationName}": the assistant module is applied directly and is not managed through the shared configuration. No changes were saved.`);
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
                    return;
                }

                const existingManagedAppliedTemplate = getManagedLocationAppliedTemplate(
                    appliedTemplatesResponse.data ?? [],
                    props.orgContainer.state.currentOrganization.id,
                    commonAppTemplateConfigId
                );
                if (!existingManagedAppliedTemplate) {
                    console.warn(`Skipping update for "${locationName}": no shared configuration template was found for this location. It may have been set up outside of this form. No changes were saved.`);
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
                    return;
                }

                // Fetch GenAI flag for AI Ordering (uses hoisted feature flags data)
                let genAIFoodOrderingFlag: any = undefined;
                if (values.assistantType === 'ai-ordering') {
                    genAIFoodOrderingFlag = allFeatureFlagsData.find((f: any) => f.id === GenAIFoodOrdering);
                }

                const storeId = values.assistantType === 'ai-ordering'
                    ? (values.storeIds?.[locationIndex] ?? '')
                    : undefined;

                // ----------------------------------------------------------------
                // AI Ordering: handle Manage Menu app (update if exists, create if not)
                // ----------------------------------------------------------------
                let manageMenuAppId: string | undefined;
                if (values.assistantType === 'ai-ordering') {
                    const manageMenuAppName = `${locationAppFullName} - Manage Menu`;
                    props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'create-manage-menu' as LocationSubStep }));

                    // Primary check: the location app itself carries the authoritative link.
                    // If this field is populated the Manage Menu app already exists — never create another one.
                    const linkedManageMenuAppId: string | undefined = existingApp.manageMenuApplicationId;

                    // Fallback: name-based lookup in the supplied manageMenuApps list (handles legacy
                    // setups where the field may not have been persisted yet).
                    const nameMatchedManageMenuApp = !linkedManageMenuAppId
                        ? (props.manageMenuApps ?? []).find((app: any) => app.name === manageMenuAppName)
                        : undefined;

                    if (linkedManageMenuAppId) {
                        // Existing link on the location app — update the Manage Menu app in place.
                        manageMenuAppId = linkedManageMenuAppId;
                        let linkedApp: any = (props.manageMenuApps ?? []).find((app: any) => app.id === manageMenuAppId);
                        if (!linkedApp) {
                            // Not present in the supplied list (e.g., paging/filtering); fetch the authoritative copy.
                            const fetchedApp = await findApplicationWithFeatures(manageMenuAppId);
                            linkedApp = fetchedApp?.data ?? null;
                        }
                        // Preserve all existing flags and ensure the two required flags are present.
                        const existingManageMenuFlags: any[] = linkedApp?.featureFlags ?? [];
                        const manageMenuFlagsToSet = buildManageMenuFlagsToSet(existingManageMenuFlags);
                        const updateManageMenuResult = await updateApplication(manageMenuAppId, {
                            name: linkedApp?.name ?? manageMenuAppName,
                            shortDescription: linkedApp?.shortDescription ?? toShortDescription(manageMenuAppName),
                            description: linkedApp?.description ?? manageMenuAppName,
                            keywords: linkedApp?.keywords,
                            invocationPhrase: linkedApp?.invocationPhrase ?? manageMenuAppName,
                            imageItemId: linkedApp?.imageItemId,
                            featureFlags: manageMenuFlagsToSet,
                            posName: values.posNameOrdering,
                            posLocationId: storeId
                        });
                        if (updateManageMenuResult.resultType !== 'Ok') {
                            throw new Error(`Could not save the ordering menu app "${linkedApp?.name ?? manageMenuAppName}" for location "${locationName}". The server returned: ${updateManageMenuResult.errors?.join(', ') || updateManageMenuResult.resultType}. The POS name and store ID were not updated.`);
                        }
                    } else if (nameMatchedManageMenuApp) {
                        // Legacy fallback: no ID on the location app but found by name — update it and
                        // persist the link back onto the location app so future saves use the ID path.
                        manageMenuAppId = nameMatchedManageMenuApp.id;
                        // Preserve all existing flags and ensure the two required flags are present.
                        const existingNameMatchedFlags: any[] = nameMatchedManageMenuApp.featureFlags ?? [];
                        const nameMatchedFlagsToSet = buildManageMenuFlagsToSet(existingNameMatchedFlags);
                        const updateNameMatchedResult = await updateApplication(manageMenuAppId, {
                            name: nameMatchedManageMenuApp.name,
                            shortDescription: nameMatchedManageMenuApp.shortDescription,
                            description: nameMatchedManageMenuApp.description,
                            keywords: nameMatchedManageMenuApp.keywords,
                            invocationPhrase: nameMatchedManageMenuApp.invocationPhrase,
                            imageItemId: nameMatchedManageMenuApp.imageItemId,
                            featureFlags: nameMatchedFlagsToSet,
                            posName: values.posNameOrdering,
                            posLocationId: storeId
                        });
                        if (updateNameMatchedResult.resultType !== 'Ok') {
                            throw new Error(`Could not save the ordering menu app "${nameMatchedManageMenuApp.name}" for location "${locationName}". The server returned: ${updateNameMatchedResult.errors?.join(', ') || updateNameMatchedResult.resultType}. The POS name and store ID were not updated.`);
                        }
                        // Persist the link onto the location app so the ID path is used going forward.
                        const linkBackResult = await updateApplication(locationAppId, {
                            name: existingApp.name,
                            shortDescription: existingApp.shortDescription,
                            description: existingApp.description,
                            keywords: existingApp.keywords,
                            invocationPhrase: existingApp.invocationPhrase,
                            imageItemId: existingApp.imageItemId,
                            featureFlags: existingApp.featureFlags,
                            manageMenuApplicationId: manageMenuAppId
                        });
                        if (linkBackResult.resultType !== 'Ok') {
                            throw new Error(`Could not link the ordering menu app to location "${locationName}". The server returned: ${linkBackResult.errors?.join(', ') || linkBackResult.resultType}. Future saves may create a duplicate ordering menu app.`);
                        }
                    } else {
                        // Truly net-new: no existing link and no name match — create the Manage Menu app.
                        const manageMenuAppResponse = await createEmptyApplication(props.orgContainer.state.currentOrganization.id, {
                            name: manageMenuAppName,
                            shortDescription: toShortDescription(manageMenuAppName),
                            description: manageMenuAppName,
                            invocationPhrase: manageMenuAppName
                        });
                        if (manageMenuAppResponse.resultType !== 'Ok' || !manageMenuAppResponse.data?.id) {
                            throw new Error(`Could not create the ordering menu app for location "${locationName}". The server returned: ${manageMenuAppResponse.errors?.join(', ') || manageMenuAppResponse.resultType}. No ordering menu app was created and no changes were saved for this location.`);
                        }
                        manageMenuAppId = manageMenuAppResponse.data.id;
                        const configureNewManageMenuResult = await updateApplication(manageMenuAppId, {
                            name: manageMenuAppResponse.data.name,
                            shortDescription: manageMenuAppResponse.data.shortDescription,
                            description: manageMenuAppResponse.data.description,
                            keywords: manageMenuAppResponse.data.keywords,
                            invocationPhrase: manageMenuAppResponse.data.invocationPhrase,
                            imageItemId: manageMenuAppResponse.data.imageItemId,
                            featureFlags: [{ id: FoodOrderingFeatureFlagId }],
                            posName: values.posNameOrdering,
                            posLocationId: storeId
                        });
                        if (configureNewManageMenuResult.resultType !== 'Ok') {
                            throw new Error(`Could not apply settings to the new ordering menu app "${manageMenuAppName}" for location "${locationName}". The server returned: ${configureNewManageMenuResult.errors?.join(', ') || configureNewManageMenuResult.resultType}. The app was created but its feature flags and POS settings were not saved.`);
                        }
                        if (genAIFoodOrderingFlag) {
                            await updateApplicationFeatureFlagSettings(manageMenuAppId, {
                                featureFlagId: GenAIFoodOrdering,
                                featureFlagSettings: buildGenAISettings(genAIFoodOrderingFlag)
                            });
                        }
                        // Persist the new Manage Menu app ID onto the location app so future saves
                        // use the ID path and never create a duplicate.
                        const linkNewManageMenuResult = await updateApplication(locationAppId, {
                            name: existingApp.name,
                            shortDescription: existingApp.shortDescription,
                            description: existingApp.description,
                            keywords: existingApp.keywords,
                            invocationPhrase: existingApp.invocationPhrase,
                            imageItemId: existingApp.imageItemId,
                            featureFlags: existingApp.featureFlags,
                            manageMenuApplicationId: manageMenuAppId
                        });
                        if (linkNewManageMenuResult.resultType !== 'Ok') {
                            throw new Error(`Could not link the new ordering menu app to location "${locationName}". The server returned: ${linkNewManageMenuResult.errors?.join(', ') || linkNewManageMenuResult.resultType}. Future saves may create a duplicate ordering menu app.`);
                        }
                    }
                }

                if (values.assistantType === 'dental-scheduling') {
                    const updateApplicationRequest = {
                        name: existingApp.name,
                        shortDescription: existingApp.shortDescription,
                        description: existingApp.description,
                        keywords: existingApp.keywords,
                        invocationPhrase: existingApp.invocationPhrase,
                        imageItemId: existingApp.imageItemId,
                        featureFlags: existingApp.featureFlags,
                        pmsName: values.pmsName,
                        pmsLocationId: values.storeIds?.[locationIndex] ?? undefined,
                    };
                    const updateCallDirectionResult = await updateApplication(locationAppId, updateApplicationRequest);
                    if (updateCallDirectionResult.resultType !== 'Ok') {
                        throw new Error(`Could not update the settings for "${locationName}". The server returned: ${updateCallDirectionResult.errors?.join(', ') || updateCallDirectionResult.resultType}.`);
                    }
                }

                // For AI Ordering, always PUT the location app to keep posLocationId, posName,
                // and manageMenuApplicationId in sync with whatever the user just submitted.
                // Also ensures DisableLegacyCheckoutFlowFeatureFlagId is present without
                // replacing any other flags that already exist on the app.
                if (values.assistantType === 'ai-ordering') {
                    const existingLocationFlags: any[] = existingApp.featureFlags ?? [];
                    const hasDisableLegacyOnApp = existingLocationFlags.some((f: any) => f.id === DisableLegacyCheckoutFlowFeatureFlagId);
                    // Merge DisableLegacy flag in if missing — preserve every other existing flag.
                    const appFlagsForUpdate = hasDisableLegacyOnApp
                        ? existingLocationFlags
                        : [...existingLocationFlags, { id: DisableLegacyCheckoutFlowFeatureFlagId }];
                    const updateAiOrderingResult = await updateApplication(locationAppId, {
                        name: existingApp.name,
                        shortDescription: existingApp.shortDescription,
                        description: existingApp.description,
                        keywords: existingApp.keywords,
                        invocationPhrase: existingApp.invocationPhrase,
                        imageItemId: existingApp.imageItemId,
                        featureFlags: appFlagsForUpdate,
                        posName: values.posNameOrdering,
                        posLocationId: storeId,
                        manageMenuApplicationId: existingApp.manageMenuApplicationId ?? manageMenuAppId
                    });
                    if (updateAiOrderingResult.resultType !== 'Ok') {
                        throw new Error(`Could not save the AI ordering settings for "${locationName}". The server returned: ${updateAiOrderingResult.errors?.join(', ') || updateAiOrderingResult.resultType}. The POS name, store ID, and checkout settings may not have been updated.`);
                    }

                    const environmentsResponse = await getApplicationEnvironments(locationAppId);
                    const productionEnv = environmentsResponse.data?.find((env: any) => env.name === 'Production');
                    if (productionEnv) {
                        const existingEnvFlags: any[] = productionEnv.featureFlags ?? [];
                        const hasDisableLegacy = existingEnvFlags.some((f: any) => f.id === DisableLegacyCheckoutFlowFeatureFlagId);
                        if (!hasDisableLegacy) {
                            await updateEnvironmentSettings(productionEnv.id, {
                                name: productionEnv.name,
                                applicationName: existingApp.name,
                                featureFlags: [
                                    ...existingEnvFlags,
                                    { id: DisableLegacyCheckoutFlowFeatureFlagId }
                                ]
                            });
                        }
                    }
                }

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'applying-template' as LocationSubStep }));

                // Re-apply template values — use the org template config that was resolved once
                // before the location loop (passed in as a parameter) instead of fetching it again.
                const managedAppliedTemplate = getManagedLocationAppliedTemplate(
                    appliedTemplatesResponse.data ?? [],
                    props.orgContainer.state.currentOrganization.id,
                    commonAppTemplateConfigId
                );

                if (managedAppliedTemplate && orgTemplateConfigForLocation) {
                    const defaultConfig = getDefaultAssistantConfig(
                        values.assistantType,
                        values.pmsName || values.posNameOrdering,
                        locationName,
                        locationAppId,
                        existingApp.secret ?? "",
                        values.storeIds?.[locationIndex]
                    );

                    // AI autofill for existing location app on update — mirrors the create path.
                    // Only runs when this specific location has autogenerate enabled. Per-location URL
                    // falls back to org-level URL when not specified.
                    // sharedAutofillVariables ensures variables already fetched for the common app
                    // are not called a second time.
                    let mergedConfig: Record<string, string> = { ...defaultConfig };
                    // Set of associatedVariables that the AI actually produced values for this run.
                    // Only these variables will overwrite existing saved values; everything else is preserved.
                    let autofillVariables = new Set<string>();
                    if (values.locationAutogenerateEnabled?.[locationIndex]) {
                        const locationUrl = values.locationWebsiteUrls?.[locationIndex] || values.websiteUrl || '';
                        const locationAutofillResult = await resolveAiAutofillConfig(
                            defaultConfig,
                            orgTemplateConfigForLocation.templateForm.templateFormSections,
                            locationName,
                            locationUrl,
                            sharedAutofillVariables
                        );
                        mergedConfig = locationAutofillResult.config;
                        autofillVariables = locationAutofillResult.succeededVariables;
                        allAutofillErrors.push(...locationAutofillResult.skippedWithErrors);
                        allAutofillSuccesses.push(...locationAutofillResult.succeededFields);
                    }

                    for (const appliedTemplate of [managedAppliedTemplate]) {
                        const existingFieldValues = appliedTemplate.templateFormFieldValues ?? [];

                        const templateSections = managedAppliedTemplate.templateForm?.templateFormSections?.length
                            ? managedAppliedTemplate.templateForm.templateFormSections
                            : orgTemplateConfigForLocation.templateForm.templateFormSections;
                        const allSectionFields = templateSections.flatMap(s => s.templateFormFields ?? []);
                        const templateFieldValues: TemplateFormFieldValueModel[] = [];
                        // Incremental map for O(N) conditional default evaluation.
                        const incrementalUpdateMap: Record<string, string | undefined> = {};
                        for (const section of templateSections) {
                            for (const formField of section.templateFormFields) {
                                const fieldTitle = formField.title;

                                const existingFieldValue = findFieldValueForField(formField, existingFieldValues);

                                const isFieldSelected = values.selectedFormFields.some(
                                    (selectedField: any) => fieldsMatch(selectedField, formField)
                                );
                                const isFieldDeselected = values.deselectedFormFields.some(
                                    (deselectedField: any) => fieldsMatch(deselectedField, formField)
                                );

                                let fieldValue = resolveLocationFieldValue({
                                    formField,
                                    isFieldSelected,
                                    isFieldDeselected,
                                    existingValue: existingFieldValue?.value,
                                    defaultConfig: mergedConfig,
                                    autofillVariables,
                                    configuredLocationId: (values.assistantType === 'dental-scheduling'
                                        || values.assistantType === 'ai-ordering')
                                        ? values.storeIds?.[locationIndex]
                                        : undefined,
                                    // For fields newly moved into location-specific config, carry
                                    // over the value that was in the shared config before it was
                                    // stamped with a sentinel.  findFieldValueForField matches by
                                    // associatedVariable first, then templateFormFieldId, then title.
                                    sharedConfigValue: (() => {
                                        const match = findFieldValueForField(formField, sharedConfigPreStampValues);
                                        // Ignore the value if it is itself a sentinel string.
                                        return (match?.value && match.value !== formField.associatedVariable)
                                            ? match.value
                                            : undefined;
                                    })(),
                                });

                                // For newly-added fields (no existing value), apply conditional defaults
                                // as a fallback so cross-field logic is respected on first save.
                                // Use the incremental map — no array rescan per field.
                                if ((fieldValue === formField.defaultValue || fieldValue === undefined) && existingFieldValue?.value === undefined) {
                                    const conditionalDefault = evaluateConditionalDefault(formField, allSectionFields, templateFieldValues, incrementalUpdateMap);
                                    if (conditionalDefault !== undefined) {
                                        fieldValue = conditionalDefault;
                                    }
                                }

                                templateFieldValues.push({
                                    value: fieldValue,
                                    fieldType: formField.fieldType,
                                    tip: formField.tip,
                                    title: formField.title,
                                    label: formField.label,
                                    defaultValue: formField.defaultValue,
                                    associatedVariable: formField.associatedVariable,
                                    placeholder: formField.placeholder,
                                    templateFormFieldId: formField.id
                                });

                                // Keep the incremental map current.
                                if (formField.associatedVariable) {
                                    incrementalUpdateMap[formField.associatedVariable] = fieldValue;
                                }
                            }
                        }

                        const updateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                            templateFormFieldValues: templateFieldValues,
                            dynamicFormSections: appliedTemplate.dynamicFormSections,
                            name: appliedTemplate.name,
                        };
                        await updateAppliedApplicationTemplate(appliedTemplate.id, updateRequest);
                    }
                } else if (appliedTemplatesResponse.data?.length > 0 && !orgTemplateConfigForLocation) {
                    // Applied templates exist but the org template config can't be matched to the current
                    // shared configuration app. This happens when a location app is linked to a different
                    // template config than the one currently in use. Skip the field update entirely to
                    // prevent wiping the location app's existing values.
                    console.warn(`Skipping field update for "${locationName}": org template config not found (commonAppTemplateConfigId: ${commonAppTemplateConfigId}). The location app may be linked to a different template config.`);
                } else if (appliedTemplatesResponse.data?.length > 0 && !managedAppliedTemplate) {
                    console.warn(`Skipping field update for "${locationName}": no org-owned applied template found.`);
                } else if (!appliedTemplatesResponse.data?.length) {
                    // No applied template yet — add one (handles edge case where template wasn't applied on creation).
                    // Reuse the hoisted orgTemplateConfigForLocation rather than re-fetching the same data.
                    const appliedTemplateResponse = await addAppliedApplicationTemplate(locationAppId, commonAppTemplateConfigId);
                    const refreshedConfig = orgTemplateConfigForLocation;

                    if (refreshedConfig) {
                        const defaultConfig = getDefaultAssistantConfig(
                            values.assistantType,
                            values.pmsName || values.posNameOrdering,
                            locationName,
                            locationAppId,
                            existingApp.secret ?? "",
                            values.storeIds?.[locationIndex]
                        );

                        const allRefreshedFields = refreshedConfig.templateForm.templateFormSections.flatMap(s => s.templateFormFields ?? []);
                        const templateFieldValues: TemplateFormFieldValueModel[] = [];
                        // Incremental map for O(N) conditional default evaluation.
                        const incrementalRefreshMap: Record<string, string | undefined> = {};
                        for (const section of refreshedConfig.templateForm.templateFormSections) {
                            for (const formField of section.templateFormFields) {
                                const fieldTitle = formField.title;
                                let fieldValue = formField.defaultValue;
                                const isFieldSelected = values.selectedFormFields.some(
                                    (selectedField: any) => fieldsMatch(selectedField, formField)
                                );
                                const configKey = formField.associatedVariable && defaultConfig[formField.associatedVariable] !== undefined
                                    ? formField.associatedVariable
                                    : fieldTitle;
                                if (isFieldSelected && defaultConfig[configKey] !== undefined) {
                                    const configValue = defaultConfig[configKey];
                                    if (configValue !== '' && configValue !== null && configValue !== undefined) {
                                        fieldValue = typeof configValue === 'string' ? configValue : String(configValue);
                                    }
                                }
                                // Fall back to conditional default if we only have the schema default.
                                // Use the incremental map — no array rescan per field.
                                if (fieldValue === formField.defaultValue || fieldValue === undefined) {
                                    const conditionalDefault = evaluateConditionalDefault(formField, allRefreshedFields, templateFieldValues, incrementalRefreshMap);
                                    if (conditionalDefault !== undefined) {
                                        fieldValue = conditionalDefault;
                                    }
                                }
                                templateFieldValues.push({
                                    value: fieldValue,
                                    fieldType: formField.fieldType,
                                    tip: formField.tip,
                                    title: formField.title,
                                    label: formField.label,
                                    defaultValue: formField.defaultValue,
                                    associatedVariable: formField.associatedVariable,
                                    placeholder: formField.placeholder,
                                    templateFormFieldId: formField.id
                                });
                                // Keep the incremental map current.
                                if (formField.associatedVariable) {
                                    incrementalRefreshMap[formField.associatedVariable] = fieldValue;
                                }
                            }
                        }
                        const updateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                            templateFormFieldValues: templateFieldValues,
                            dynamicFormSections: appliedTemplateResponse.data.dynamicFormSections,
                            name: appliedTemplateResponse.data.name,
                        };
                        await updateAppliedApplicationTemplate(appliedTemplateResponse.data.id, updateRequest);
                    }
                }

                props.setLocationProgress?.(prev => ({ ...prev, [locationName]: 'complete' as LocationSubStep }));
            };

            // create or update common app
            if (!props.commonApp) {
                await createCommonAppAndAppliedAppTemplate();
            } else {
                await updateCommonAppAndAppliedAppTemplate();
            };

            props.setProgressStep?.('configure-template');
            props.setProgressLocationName?.(getCommonAppNameForAssistantType(values.assistantType));

            await new Promise(r => setTimeout(r, 800));

            const orgTemplateConfigsResponse = await getOrganizationTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
            let orgTemplateConfig = orgTemplateConfigsResponse.data[0];
            if (orgTemplateConfigsResponse.data.length > 1) {
                const customModuleName = getCustomModuleName(values.assistantType);
                orgTemplateConfig = orgTemplateConfigsResponse.data.find(templateConfig => templateConfig.name === customModuleName);

                // Fallback to the old AI answering or dental scheduling module names if no match found
                if (!orgTemplateConfig) {
                    orgTemplateConfig = orgTemplateConfigsResponse.data.find(templateConfig =>
                        templateConfig.name === aiAnsweringCustomModuleName ||
                        templateConfig.name === aiDentalSchedulingCustomModuleName);
                }

                // Final fallback to first available
                if (!orgTemplateConfig) {
                    orgTemplateConfig = orgTemplateConfigsResponse.data[0];
                }
            }

            if (!orgTemplateConfig) {
                await createCommonAppTemplateConfiguration()
            } else {
                await updateCommonAppTemplateConfiguration()
            };

            // create or update location apps
            props.setProgressStep?.('create-locations');

            await new Promise(r => setTimeout(r, 800));

            // Fetch the org-owned template configuration once here — now that commonAppTemplateConfigId
            // is set — and pass it into the per-location helpers so they don't need to re-fetch it
            // for every location in the loop.
            const orgTemplateConfigsForLocationResponse = await getOrganizationTemplateConfigurations(props.orgContainer.state.currentOrganization.id);
            const orgTemplateConfigForLocation = orgTemplateConfigsForLocationResponse.data?.find(
                (config: any) => config.id === commonAppTemplateConfigId
            ) ?? null;

            // Initialize all location rows to 'pending' so they appear in the stepper immediately
            const activeLocationNames = values.locationNames.filter(n => n?.trim());
            props.setLocationProgress?.(
                Object.fromEntries(activeLocationNames.map(n => [n, 'pending' as LocationSubStep])) as Record<string, LocationSubStep>
            );

            for (let locationIndex = 0; locationIndex < values.locationNames.length; locationIndex++) {
                const locationName = values.locationNames[locationIndex];
                if (!locationName?.trim()) continue;
                const existingApp = props.locationApps?.find(app => app.name === locationName);
                if (existingApp) {
                    props.setProgressLocationName?.(locationName);
                    await updateExistingLocationApp(existingApp, locationName, locationIndex, orgTemplateConfigForLocation);
                    updatedNames.push(locationName);
                } else {
                    props.setProgressLocationName?.(locationName);
                    await createLocationAppAndApplyAppTemplate(locationName, locationIndex, orgTemplateConfigForLocation);
                    createdNames.push(locationName);
                }
                if (values.locationNames.length === 1) {
                    await new Promise(r => setTimeout(r, 800));
                }
            };

            props.setProgressStep?.('publishing');
            props.setProgressLocationName?.('');

            // Build differentiated success message
            const successParts: string[] = [];
            successParts.push(commonAppWasCreated ? 'Shared configuration app was created.' : 'Shared configuration app was updated.');
            if (createdNames.length > 0) {
                successParts.push(createdNames.length > 5
                    ? `Created ${createdNames.length} new location apps.`
                    : `Created ${createdNames.length} new location app${createdNames.length > 1 ? 's' : ''}: ${createdNames.join(', ')}.`);
            }
            if (updatedNames.length > 0) {
                successParts.push(updatedNames.length > 5
                    ? `Updated ${updatedNames.length} existing location apps.`
                    : `Updated ${updatedNames.length} existing location app${updatedNames.length > 1 ? 's' : ''}: ${updatedNames.join(', ')}.`);
            }
            if (provisionedPhoneNumbers.length > 0) {
                successParts.push(`Created phone numbers: ${provisionedPhoneNumbers.join(', ')}.`);
            }
            setStatus({
                type: 'success',
                message: successParts.join(' '),
                details: [],
                autofillErrors: allAutofillErrors,
                autofillSuccesses: allAutofillSuccesses,
                newlySharedFieldTitles: newlySharedFields.map(f => f.title),
                unsyncedLocations: updatedNames,
                posConfigErrors: posConfigErrors,
                phoneProvisioningErrors,
            });
            props.setIsSubmittingForm(false);

        } catch (error) {
            props.setIsSubmittingForm(false);
            props.setProgressStep?.(null);
            props.setProgressMode?.(null);
            props.setLocationProgress?.(() => ({}));

            // Set error status for the form to display
            const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred during form submission.';
            setStatus({
                type: 'error',
                message: errorMessage,
                details: []
            });
        }
    }
})(SparkLocationsForm);

export default ManageSparkLocationsForm;
